/*!
 * quest-one-v2-audio.js  -  Quest One v2 sound layer (window.QOAudio)
 *
 * Plain ES2017, no dependencies, no fetch/XHR. Music and file effects are
 * <audio> elements pointed at the Shopify Files URLs below; every other sound
 * is synthesised with the Web Audio API from small deterministic recipes.
 *
 *   QOAudio.unlock()                      call once, synchronously, inside the "Begin" tap
 *   QOAudio.music('road', {fade:1200})    equal-power crossfade to a looping track; null fades out
 *   QOAudio.prefetch('fen')               start buffering the next likely track / effect / bed
 *   QOAudio.duck(1500), QOAudio.unduck()  dip the music 40% under a big sting
 *   QOAudio.sfx('crit', {volume, rate, pan, delay, duck})
 *   QOAudio.bed('rain', true|false, {volume, fade})
 *   QOAudio.setMuted(bool), QOAudio.muted  (persisted in localStorage 'qo2-muted')
 *   QOAudio.buzz('hit' | 30 | [20,40,20])
 *   QOAudio.list() -> {music, files, synth, beds}
 *   QOAudio.state(), QOAudio.configure({...}), QOAudio.renderOffline(name), QOAudio.destroy()
 *
 * Mute changes are announced with a window 'qoaudio:mute' event, detail {muted}.
 *
 * iOS notes: WebKit ignores <audio>.volume, so when the element volume is not
 * writable the elements are routed through Web Audio gain nodes (the Shopify CDN
 * sends Access-Control-Allow-Origin: *). A runtime check falls back to plain
 * elements if routing ever comes out silent, and a file that fails to load while
 * routed is retried as a plain element. On WebKit an element is "blessed"
 * (muted play + pause) inside a tap so later timer-driven plays are allowed:
 * the unlock tap blesses the opening track, every later tap one more track in
 * the order the quest first plays them, and the file effects play from decoded
 * Web Audio buffers, which need no blessing. Chromium and Gecko unlock per
 * document, so they skip all of that and keep one element per sound.
 */
(function (win) {
  'use strict';
  if (!win || !win.document) return;
  if (win.QOAudio && win.QOAudio.__qo2) return;

  var doc = win.document;
  var nav = win.navigator || {};
  var VERSION = '1.0.0';
  var PI = Math.PI;
  function noop() {}

  /* ------------------------------------------------------------------ catalogue */

  var CDN = 'https://cdn.shopify.com/s/files/1/0884/2389/1261/files/';
  var MUSIC = {
    /* The quest's opening music (Jan, 6 October 2026): from the start screen's music button through the first questions
       (the posting, race, class, name and nature). "intro" still plays wherever it came back later in the quest. */
    hearth: CDN + 'Subtle_Hearthside_Echoes.mp3?v=1791341791',
    intro: CDN + 'FF_Intro.MP3?v=1775560465',
    road: CDN + 'FF_Milit.MP3?v=1775560782',
    fen: CDN + 'FF_Bog.MP3?v=1775561540',
    cart: CDN + 'FF_Tinker.MP3?v=1775561442',
    tavern: CDN + 'Shadows_in_the_Tankard_2.mp3?v=1775561993',
    dark: CDN + 'water_ff.MP3?v=1775562342',
    wake: CDN + 'Awaken_Song.MP3?v=1778550072'
  };
  var FILES = {
    dice: CDN + 'dr4.MP3?v=1775565290',
    success: CDN + 'success_roll_ff.MP3?v=1775563732',
    fail: CDN + 'dice_fail.MP3?v=1775563197',
    door: CDN + 'door_sfx.MP3?v=1775565209',
    pouch: CDN + 'dicerollfinal.MP3?v=1775564155',
    whew: CDN + 'whew.MP3?v=1775690724',
    janbuff: CDN + 'Jan_Buff.MP3?v=1777416601',
    coin: CDN + 'coin_flip_yost.MP3?v=1778069155',
    shatter: CDN + 'shatter.MP3?v=1778071284',
    click: CDN + 'button_sfx_basic.MP3?v=1784104242',
    /* Jan's save (Jan, 6 October 2026): her "Catch!" from the dark, then the bolt landing on the Matron. Catch.ogg
       (Files, v=1791264868) is Ogg Vorbis, which iPhones before iOS 18.4 cannot play, so it ships here as the same
       sound in AAC (trimmed to its 0.64 s, decoded in Chrome with the same onset and peak). attack.wav plays as is. */
    catch: 'data:audio/mp4;base64,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',
    bolt: CDN + 'attack.wav?v=1791264246'
  };
  /* Per-file mix level (same ballpark as the live quest: effects 0.6, button 0.3). */
  var FILE_VOL = { dice: 0.6, success: 0.6, fail: 0.55, door: 0.6, pouch: 0.6, whew: 0.6, janbuff: 0.8, coin: 0.55, shatter: 0.55, click: 0.3, catch: 0.75, bolt: 0.8 };
  /* Played from memory (decoded Web Audio buffers) once prefetched, so they start on the frame they are asked for:
     the bolt has to land with the art's impact. Until a buffer is ready they play through their <audio> element. */
  var BUF = { catch: 1, bolt: 1, janbuff: 1 };
  /* Small files needed in the first minute: buffered at unlock. Everything else waits. */
  var WARM = { click: 1, dice: 1, success: 1, fail: 1 };
  /* WebKit only (Jan, 7 October 2026: Begin was the slowest tap in the Data Hub). Begin used to start every element at
     once, eight tracks and two copies of each effect, and an iPhone froze on it. Now the unlock tap readies only the
     opening track, every later tap one more, the one the quest plays next first (NEXT_MUSIC, then BLESS_ORDER), and the
     effects load into memory one by one after Begin (BUF_ORDER) and play from there. An effect asked for before its
     buffer is in plays through an element, as before. */
  var NEXT_MUSIC = { hearth: 'intro', road: 'intro', fen: 'intro', cart: 'intro', intro: 'tavern', tavern: 'dark', dark: 'wake', wake: 'intro' };
  var BLESS_ORDER = ['hearth', 'intro', 'tavern', 'dark', 'wake', 'road', 'fen', 'cart'];
  var BUF_ORDER = ['click', 'dice', 'success', 'fail', 'coin', 'door', 'whew', 'shatter', 'janbuff', 'catch', 'bolt', 'pouch'];
  /* Big stingers dip the music on their own; pass {duck:0} to opt out. */
  var AUTO_DUCK = { boom: 1900, crit: 1100, reveal: 1500, heal: 1000, success: 1300, janbuff: 2400, shatter: 900, bolt: 900 };
  /* Dropped first when too many voices overlap. */
  var LOW_PRI = { tap: 1, select: 1, pen: 1, page: 1, glint: 1, gold: 1 };
  /* Rain at half its first level (Jan, 30 September: still too loud). The quest no longer plays fire or crowd. */
  var BED_LEVEL = { crowd: 0.42, rain: 0.14, fire: 0.4 };
  var BED_NEEDS = { crowd: ['pink', 'white'], rain: ['white', 'pink', 'drops1', 'drops2'], fire: ['brown', 'pink', 'white', 'crack1', 'crack2'] };
  var BUZZ = {
    tap: 8, select: 12, stamp: 18, hit: 24, heavy: 40, success: [16, 40, 26],
    crit: [18, 40, 26, 40, 60], fail: [40, 60, 40], fumble: [40, 60, 40], heartbeat: [28, 120, 20], boom: [60, 40, 30]
  };

  var cfg = { musicVolume: 0.4, sfxVolume: 1, duckDepth: 0.4, fade: 1200, route: 'auto' };

  /* ------------------------------------------------------------------ utilities */

  function now() { return (win.performance && win.performance.now) ? win.performance.now() : Date.now(); }
  function clamp(x, a, b) { return x < a ? a : (x > b ? b : x); }
  function num(v, d) { if (v == null || v === '') return d; v = +v; return isFinite(v) ? v : d; }
  function has(o, k) { return Object.prototype.hasOwnProperty.call(o, k); }
  function stepTo(v, target, amt) { return v < target ? Math.min(target, v + amt) : Math.max(target, v - amt); }
  function smooth(x) { return x * x * (3 - 2 * x); }
  function tanh(x) { if (Math.tanh) return Math.tanh(x); var e = Math.exp(2 * x); return (e - 1) / (e + 1); }
  var warned = {};
  function warnOnce(id, msg) {
    if (warned[id]) return;
    warned[id] = 1;
    try { if (win.console && win.console.warn) win.console.warn('[QOAudio] ' + msg); } catch (e) {}
  }
  function report(e) { var m = (e && e.message) ? e.message : String(e); warnOnce('e:' + m, 'recovered from: ' + m); }
  function later(fn, ms) { return setTimeout(function () { try { fn(); } catch (e) { report(e); } }, ms); }
  function idle(fn) {
    if (win.requestIdleCallback) { try { win.requestIdleCallback(function () { try { fn(); } catch (e) { report(e); } }, { timeout: 1500 }); return; } catch (e) {} }
    later(fn, 60);
  }
  /* Set-up that needs no tap runs once the tap's frame is drawn, so the button answers first (Jan, 19 October 2026:
     Begin was still the slowest tap on phones). A hidden page draws no frames, so a timer stands in. */
  function afterPaint(fn) {
    var done = false;
    function go() { if (done) return; done = true; try { fn(); } catch (e) { report(e); } }
    try { win.requestAnimationFrame(function () { setTimeout(go, 0); }); } catch (e) {}
    setTimeout(go, 120);
  }
  function hash(str) {
    var h = 2166136261;
    for (var i = 0; i < str.length; i++) { h ^= str.charCodeAt(i); h = Math.imul(h, 16777619); }
    return h >>> 0;
  }
  /* mulberry32: tiny seeded PRNG so every recipe renders identically each time. */
  function rng(seed) {
    var a = seed >>> 0;
    return function () {
      a = (a + 0x6D2B79F5) >>> 0;
      var t = a;
      t = Math.imul(t ^ (t >>> 15), t | 1);
      t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }
  function readMuted() { try { return win.localStorage.getItem('qo2-muted') === '1'; } catch (e) { return false; } }
  function writeMuted(m) { try { win.localStorage.setItem('qo2-muted', m ? '1' : '0'); } catch (e) {} }
  function emit(name, detail) {
    try {
      var ev;
      if (typeof win.CustomEvent === 'function') ev = new win.CustomEvent('qoaudio:' + name, { detail: detail });
      else { ev = doc.createEvent('CustomEvent'); ev.initCustomEvent('qoaudio:' + name, false, false, detail); }
      win.dispatchEvent(ev);
    } catch (e) {}
  }

  /* ------------------------------------------------------------------ state */

  var S = {
    unlocked: false, destroyed: false, muted: readMuted(), hidden: !!doc.hidden,
    ctx: null, bus: null, route: false, volFixed: false, health: '',
    tracks: {}, cur: null, pools: {},
    beds: {}, want: {}, bedPending: {},
    muteM: 1, duckD: 0, duckUntil: 0,
    voices: 0, last: {}, lastAct: 0, idleTimer: 0,
    needBless: false, inGesture: false, listening: false, reviveOnTap: false,
    gate: perElementGate(), srcs: [], bufsWarm: false
  };
  S.muteM = S.muted ? 0 : 1;
  /* WebKit (Safari, and every iOS browser and in-app webview) allows each media
     element separately after it has been started inside a gesture. Chromium and
     Gecko unlock the whole document on the first tap, so blessing there would only
     start 27 needless downloads; they bless an element only if it is ever refused. */
  function perElementGate() {
    try { if (/Apple/i.test(nav.vendor || '')) return true; } catch (e) {}
    return !volumeWritable();
  }

  /* ------------------------------------------------------------------ background jobs
     Noise tables and the reverb impulse are filled in small slices so no frame
     spends more than ~5 ms on them. A synchronous caller can finish a table early. */

  var jobs = [], jobTimer = 0;
  function queueJob(fn) { jobs.push(fn); if (!jobTimer) jobTimer = setTimeout(runJobs, 0); }
  function runJobs() {
    jobTimer = 0;
    var t0 = now();
    while (jobs.length && now() - t0 < 5) {
      var done = true;
      try { done = jobs[0](); } catch (e) { report(e); }
      if (done) jobs.shift();
    }
    if (jobs.length) jobTimer = setTimeout(runJobs, 12);
  }

  var NB = {};
  var NOISE = { white: [2.6, 11], pink: [3.3, 23], brown: [3.7, 37] };
  function noiseEntry(c, kind) {
    var sr = c.sampleRate, key = kind + '@' + sr, e = NB[key];
    if (e) return e;
    var spec = NOISE[kind], L = Math.round(spec[0] * sr), X = Math.round(0.06 * sr);
    var buf = c.createBuffer(1, L, sr), d = buf.getChannelData(0), tail = new Float32Array(X);
    var r = rng(spec[1]), pos = 0, sum = 0, b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0, last = 0;
    e = { buf: buf, done: false, queued: false };
    e.step = function (max) {
      if (e.done) return true;
      var end = Math.min(L + X, pos + max), w, v;
      for (; pos < end; pos++) {
        w = r() * 2 - 1;
        if (kind === 'pink') {
          b0 = 0.99886 * b0 + w * 0.0555179; b1 = 0.99332 * b1 + w * 0.0750759; b2 = 0.969 * b2 + w * 0.153852;
          b3 = 0.8665 * b3 + w * 0.3104856; b4 = 0.55 * b4 + w * 0.5329522; b5 = -0.7616 * b5 - w * 0.016898;
          v = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + w * 0.5362) * 0.11; b6 = w * 0.115926;
        } else if (kind === 'brown') { last = (last + 0.02 * w) / 1.02; v = last * 3.5; }
        else v = w;
        if (pos < L) { d[pos] = v; sum += v; } else tail[pos - L] = v;
      }
      if (pos >= L + X) {
        /* Remove DC, then crossfade the loop seam into the natural continuation so looping beds never click. */
        var mean = sum / L, i, pk = 0, a, m;
        for (i = 0; i < L; i++) d[i] -= mean;
        for (i = 0; i < X; i++) { a = (i + 0.5) / X; d[i] = d[i] * Math.sin(a * PI / 2) + (tail[i] - mean) * Math.cos(a * PI / 2); }
        for (i = 0; i < L; i++) { m = d[i] < 0 ? -d[i] : d[i]; if (m > pk) pk = m; }
        if (pk > 0.98) { var s = 0.98 / pk; for (i = 0; i < L; i++) d[i] *= s; }
        tail = null; e.done = true;
      }
      return e.done;
    };
    NB[key] = e;
    return e;
  }
  function noiseBuf(c, kind) { var e = noiseEntry(c, kind); if (!e.done) e.step(Infinity); return e.buf; }
  function stepper(e, n) { return function () { return e.step(n); }; }
  function prepare(c, kinds, cb) {
    var wait = false;
    for (var i = 0; i < kinds.length; i++) {
      var sp = has(SPARSE_DEF, kinds[i]), e = sp ? sparseEntry(c, kinds[i]) : noiseEntry(c, kinds[i]);
      if (!e.done) { wait = true; if (!e.queued) { e.queued = true; queueJob(stepper(e, sp ? 40 : 16384)); } }
    }
    if (!wait) { cb(); return; }
    queueJob(function () { cb(); return true; });
  }

  /* A 1.7 s stereo hall: early reflections, then darkening, decaying diffuse noise. */
  function irEntry(c) {
    var sr = c.sampleRate, key = 'ir@' + sr;
    if (NB[key]) return NB[key];
    var L = Math.round(1.7 * sr), buf = c.createBuffer(2, L, sr), A = buf.getChannelData(0), B = buf.getChannelData(1);
    var r = rng(4242), pre = Math.round(0.011 * sr), pos = 0, l0 = 0, l1 = 0;
    var e = { buf: buf, done: false, queued: false };
    e.step = function (max) {
      if (e.done) return true;
      var end = Math.min(L, pos + max);
      for (; pos < end; pos++) {
        if (pos < pre) continue;
        var tt = (pos - pre) / sr, env = Math.exp(-6.9 * tt / 1.6), cf = 0.85 - 0.62 * Math.min(1, tt / 1.3), fin = tt < 0.03 ? tt / 0.03 : 1;
        l0 += cf * ((r() * 2 - 1) - l0);
        l1 += cf * ((r() * 2 - 1) - l1);
        A[pos] = l0 * env * fin;
        B[pos] = l1 * env * fin;
      }
      if (pos >= L) {
        var taps = [[0.013, 0.5], [0.019, -0.42], [0.027, 0.36], [0.034, -0.3], [0.047, 0.26], [0.061, -0.2], [0.077, 0.16]];
        for (var i = 0; i < taps.length; i++) {
          var x = Math.round(taps[i][0] * sr), y = Math.round((taps[i][0] + (i % 2 ? 0.0017 : -0.0013)) * sr);
          if (x < L) A[x] += taps[i][1];
          if (y < L) B[y] += taps[i][1] * 0.9;
        }
        var fo = Math.round(0.05 * sr);
        for (var j = 0; j < fo; j++) { var g = j / fo; A[L - 1 - j] *= g; B[L - 1 - j] *= g; }
        e.done = true;
      }
      return e.done;
    };
    NB[key] = e;
    return e;
  }

  /* Sparse event tables for the beds (rain drops, hearth crackle), stored at 24 kHz.
     Events are planned up front (cheap random draws) and rendered a few dozen at a
     time, so building a bed never stalls a frame. Each event carries its own seed,
     so the result is identical however the work is sliced. */
  var SP = {};
  var SPARSE_DEF = {
    drops1: [3.7, 7919, dropPlan], drops2: [4.9, 15838, dropPlan],
    crack1: [5.3, 104729, crackPlan], crack2: [7.1, 209458, crackPlan]
  };
  function sparseEntry(c, name) {
    if (SP[name]) return SP[name];
    var def = SPARSE_DEF[name], sr = 24000, n, b;
    try { n = Math.round(sr * def[0]); b = c.createBuffer(2, n, sr); }
    catch (x) { sr = c.sampleRate; n = Math.round(sr * def[0]); b = c.createBuffer(2, n, sr); }
    var L = b.getChannelData(0), R = b.getChannelData(1), evs = def[2](rng(def[1]), def[0]), i = 0;
    var e = { buf: b, done: false, queued: false };
    e.step = function (max) {
      if (e.done) return true;
      var end = Math.min(evs.length, i + max);
      for (; i < end; i++) evs[i](L, R, n, sr);
      if (i >= evs.length) { e.done = true; evs = null; }
      return e.done;
    };
    SP[name] = e;
    return e;
  }
  function sparse(c, name) { var e = sparseEntry(c, name); if (!e.done) e.step(Infinity); return e.buf; }
  function dropPlan(r, sec) {
    var evs = [], count = Math.round(sec * 42);
    for (var i = 0; i < count; i++) {
      var at = r(), big = r() < 0.09, f = big ? 800 + r() * 900 : 2000 + r() * 3400;
      var dur = big ? 0.012 + r() * 0.014 : 0.003 + r() * 0.005;
      var amp = big ? 0.12 + r() * 0.18 : 0.03 + Math.pow(r(), 3) * 0.22;
      evs.push(dropEv(at, big, f, dur, amp, r() * PI / 2, r() * 2 * PI));
    }
    return evs;
  }
  function dropEv(at, big, f, dur, amp, th, ph) {
    return function (L, R, n, sr) {
      var len = Math.max(8, Math.floor(dur * sr)), x0 = Math.floor(at * n), gl = Math.cos(th) * amp, gr = Math.sin(th) * amp;
      var dec = Math.exp(-4 / len), e = 1, p = ph;
      for (var j = 0; j < len; j++) {
        p += 2 * PI * (big ? f * (1 + 0.8 * j / len) : f) / sr;
        var s = Math.sin(p) * e * (j < 6 ? j / 6 : 1), x = (x0 + j) % n;
        L[x] += s * gl; R[x] += s * gr; e *= dec;
      }
    };
  }
  function popEv(at, amp, len, width, seed) {
    return function (L, R, n) {
      var q = rng(seed), th = PI / 4 + (q() * 2 - 1) * width, gl = Math.cos(th) * amp, gr = Math.sin(th) * amp;
      var dec = Math.exp(-3 / len), e = 1, prev = 0, x0 = Math.floor(at * n);
      for (var j = 0; j < len; j++) {
        var w0 = q() * 2 - 1, v = (w0 - prev * 0.6) * e, x = (x0 + j) % n;
        prev = w0; L[x] += v * gl; R[x] += v * gr; e *= dec;
      }
    };
  }
  function knockEv(at, f, a) {
    return function (L, R, n, sr) {
      var len = Math.floor(0.012 * sr), x0 = Math.floor(at * n), dec = Math.exp(-3 / len), e = a, p = 0;
      for (var j = 0; j < len; j++) { p += 2 * PI * f / sr; var v = Math.sin(p) * e, x = (x0 + j) % n; L[x] += v; R[x] += v; e *= dec; }
    };
  }
  function crackPlan(r, sec) {
    var evs = [], i, j, seed = function () { return Math.floor(r() * 4294967295); };
    var clusters = Math.round(sec * 2.4);
    for (i = 0; i < clusters; i++) {
      var tt = r() * sec, pops = 1 + Math.floor(r() * r() * 6);
      for (j = 0; j < pops; j++) { evs.push(popEv(tt / sec, (j ? 0.12 : 0.25) + r() * r() * 0.7, 6 + Math.floor(r() * 60), 0.35, seed())); tt += 0.004 + r() * 0.05; }
    }
    var micro = Math.round(sec * 28);
    for (i = 0; i < micro; i++) evs.push(popEv(r(), 0.02 + r() * 0.05, 3 + Math.floor(r() * 10), 0.5, seed()));
    var knocks = Math.round(sec * 0.6);
    for (i = 0; i < knocks; i++) {
      var kt = r(), a = 0.15 + r() * 0.15;
      evs.push(knockEv(kt, 160 + r() * 160, a));
      evs.push(popEv(kt, a, 8, 0.2, seed()));
    }
    return evs;
  }

  /* Karplus-Strong pluck, rendered once per pitch (about 0.2 ms). */
  var PL = {};
  function pluckBuf(c, f) {
    var sr = c.sampleRate, key = f + '@' + sr;
    if (PL[key]) return PL[key];
    var n = Math.floor(sr * 0.8), buf = c.createBuffer(1, n, sr), d = buf.getChannelData(0);
    var N = Math.max(2, Math.round(sr / f - 0.5)), line = new Float32Array(N), r = rng(hash(key)), lp = 0, mean = 0, i;
    for (i = 0; i < N; i++) { lp += 0.55 * ((r() * 2 - 1) - lp); line[i] = lp; mean += lp; }
    mean /= N;
    for (i = 0; i < N; i++) line[i] -= mean;
    var idx = 0, peak = 0;
    for (i = 0; i < n; i++) {
      var a = line[idx], nx = idx + 1 === N ? 0 : idx + 1;
      line[idx] = 0.4985 * (a + line[nx]);
      d[i] = a;
      if (a > peak) peak = a; else if (-a > peak) peak = -a;
      idx = nx;
    }
    if (peak > 0) { var s = 0.9 / peak; for (i = 0; i < n; i++) d[i] *= s; }
    PL[key] = buf;
    return buf;
  }

  var CURVES = {};
  function curve(name, n, fn) {
    if (CURVES[name]) return CURVES[name];
    var a = new Float32Array(n);
    for (var i = 0; i < n; i++) a[i] = fn(i / (n - 1) * 2 - 1);
    CURVES[name] = a;
    return a;
  }
  /* Linear up to 0.8, then a soft knee to 1.0 (the input is pre-scaled by 0.5, so it handles up to 2.0). */
  function clipCurve() { return curve('clip', 4097, function (x) { var u = x * 2, m = Math.abs(u); return m < 0.8 ? u : (u < 0 ? -1 : 1) * (0.8 + 0.2 * tanh((m - 0.8) / 0.2)); }); }
  function driveCurve() { var n = tanh(2.2); return curve('drive', 1025, function (x) { return tanh(2.2 * x) / n; }); }
  function reluCurve() { return curve('relu', 257, function (x) { return x > 0.15 ? (x - 0.15) / 0.85 : 0; }); }

  /* ------------------------------------------------------------------ output bus
     synth voices + beds + reverb return -> 0.5 -> soft clip -> out (mute gain) -> speakers */

  function buildBus(c) {
    var b = {};
    b.out = c.createGain(); b.out.connect(c.destination);
    b.pre = c.createGain(); b.pre.gain.value = 0.5;
    b.clip = c.createWaveShaper(); b.clip.curve = clipCurve();
    try { b.clip.oversample = 'none'; } catch (e) {}
    b.pre.connect(b.clip); b.clip.connect(b.out);
    b.sfx = c.createGain(); b.sfx.connect(b.pre);
    b.beds = c.createGain(); b.beds.connect(b.pre);
    b.revIn = c.createGain();
    b.conv = c.createConvolver();
    b.revOut = c.createGain(); b.revOut.gain.value = 0.55;
    b.revIn.connect(b.conv); b.conv.connect(b.revOut); b.revOut.connect(b.pre);
    b.revReady = false;
    return b;
  }

  /* ------------------------------------------------------------------ synth building blocks
     k = {c: context, o: output node, t: start time, r: pitch rate, rev: reverb input, rnd: seeded rng} */

  function mkK(c, out, t, o, name, rev) {
    var k = { c: c, o: out, t: t, r: clamp(num(o && o.rate, 1), 0.25, 4), rev: rev || null, rnd: rng(hash(name || 'x')), srcs: [] };
    k.send = function (node, amt) { if (!k.rev || !amt) return; var s = G(k, amt); node.connect(s); s.connect(k.rev); };
    return k;
  }
  function G(k, v) { var g = k.c.createGain(); g.gain.value = v == null ? 1 : v; return g; }
  function hz(k, f) { return clamp(f * k.r, 1, k.c.sampleRate * 0.45); }
  function O(k, type, f) { var o = k.c.createOscillator(); o.type = type; o.frequency.value = hz(k, f); return o; }
  function BQ(k, type, f, q) { var b = k.c.createBiquadFilter(); b.type = type; b.frequency.value = hz(k, f); if (q != null) b.Q.value = q; return b; }
  function env(p, t, a, peak, d) {
    p.setValueAtTime(0, t);
    p.linearRampToValueAtTime(peak, t + a);
    p.exponentialRampToValueAtTime(0.0001, t + a + d);
    p.setValueAtTime(0, t + a + d + 0.002);
  }
  function mixer(k, send) { var m = G(k, 1); m.connect(k.o); if (send) k.send(m, send); return m; }
  function panned(k, dest, pan) {
    if (!pan || !k.c.createStereoPanner) return dest;
    var p = k.c.createStereoPanner(); p.pan.value = clamp(pan, -1, 1); p.connect(dest); return p;
  }
  function NS(k, kind, t, dur, rate) {
    var s = k.c.createBufferSource(), b = noiseBuf(k.c, kind);
    s.buffer = b; s.loop = true;
    if (rate) s.playbackRate.value = rate;
    s.start(t, k.rnd() * b.duration * 0.8);
    s.stop(t + dur);
    return s;
  }
  function tone(k, type, f, t, a, peak, d, dest, f1, gt) {
    var o = O(k, type, f), g = G(k, 0);
    if (f1) { o.frequency.setValueAtTime(hz(k, f), t); o.frequency.exponentialRampToValueAtTime(hz(k, f1), t + (gt || d)); }
    o.connect(g); g.connect(dest);
    env(g.gain, t, a, peak, d);
    o.start(t); o.stop(t + a + d + 0.03);
    return o;
  }
  function nz(k, kind, t, a, peak, d, dest, filters) {
    var s = NS(k, kind, t, a + d + 0.03), node = s, g = G(k, 0);
    if (filters) for (var i = 0; i < filters.length; i++) { var b = BQ(k, filters[i][0], filters[i][1], filters[i][2]); node.connect(b); node = b; }
    node.connect(g); g.connect(dest);
    env(g.gain, t, a, peak, d);
    return g;
  }
  function bell(k, f, t, parts, peak, dest) {
    for (var i = 0; i < parts.length; i++) {
      var p = parts[i];
      if (f * p[0] * k.r < k.c.sampleRate * 0.45) tone(k, 'sine', f * p[0], t, 0.0015, peak * p[1], p[2], dest);
    }
  }
  function pluck(k, f, t, peak, d, dest) {
    var s = k.c.createBufferSource(), g = G(k, 0);
    s.buffer = pluckBuf(k.c, f); s.playbackRate.value = k.r;
    s.connect(g); g.connect(dest);
    g.gain.setValueAtTime(peak, t); g.gain.setValueAtTime(peak, t + d * 0.5); g.gain.exponentialRampToValueAtTime(0.0005, t + d);
    s.start(t); s.stop(t + d + 0.02);
  }
  function brass(k, f, t, peak, d, dest) {
    var lp = BQ(k, 'lowpass', f * 1.5, 1.2), g = G(k, 0);
    lp.frequency.setValueAtTime(hz(k, f * 1.5), t);
    lp.frequency.exponentialRampToValueAtTime(hz(k, f * 6), t + 0.03);
    lp.frequency.exponentialRampToValueAtTime(hz(k, f * 2.2), t + d);
    lp.connect(g); g.connect(dest);
    env(g.gain, t, 0.006, peak, d);
    var det = [-6, 6];
    for (var i = 0; i < 2; i++) { var o = O(k, 'sawtooth', f); o.detune.value = det[i]; o.connect(lp); o.start(t); o.stop(t + d + 0.05); }
    var s = O(k, 'sine', f / 2), sg = G(k, 0.6);
    s.connect(sg); sg.connect(lp); s.start(t); s.stop(t + d + 0.05);
  }
  var BELL = [[1, 1, 0.9], [2.0, 0.28, 0.5], [2.76, 0.42, 0.42], [5.4, 0.16, 0.22]];
  function glintAt(k, t, amp, dest) {
    bell(k, 1567.98, t, BELL, 0.07 * amp, panned(k, dest, -0.25));
    bell(k, 2349.32, t + 0.075, BELL, 0.055 * amp, panned(k, dest, 0.3));
    nz(k, 'white', t, 0.006, 0.03 * amp, 0.25, dest, [['highpass', 7000, 0.7]]);
  }

  /* ------------------------------------------------------------------ synth recipes
     Each returns its length in seconds. Levels are calibrated offline so the
     loudest 50 ms of a UI tick sits near -24 dBFS and a big sting near -14 dBFS. */

  var SYN = {
    /* soft wooden tick */
    tap: function (k) {
      var t = k.t, lp = BQ(k, 'lowpass', 5200, 0.5);
      lp.connect(k.o);
      tone(k, 'sine', 1180, t, 0.0015, 0.3, 0.055, lp, 820, 0.03);
      tone(k, 'sine', 2610, t, 0.001, 0.07, 0.03, lp);
      nz(k, 'white', t, 0.0008, 0.16, 0.012, lp, [['bandpass', 3000, 1.1]]);
      return 0.12;
    },
    /* warm two-note pluck, a rising fifth */
    select: function (k) {
      var t = k.t, lp = BQ(k, 'lowpass', 3000, 0.6);
      lp.connect(k.o); k.send(lp, 0.12);
      pluck(k, 440, t, 0.21, 0.55, lp);
      pluck(k, 659.25, t + 0.065, 0.18, 0.6, lp);
      tone(k, 'sine', 220, t, 0.002, 0.07, 0.07, lp, 160, 0.05);
      return 0.75;
    },
    /* rubber stamp pressed onto paper, then lifted */
    stamp: function (k) {
      var t = k.t, m = mixer(k, 0.08);
      tone(k, 'sine', 150, t, 0.002, 0.28, 0.18, m, 50, 0.09);
      tone(k, 'triangle', 260, t, 0.002, 0.22, 0.09, m, 120, 0.06);
      tone(k, 'sine', 430, t, 0.001, 0.2, 0.07, m, 260, 0.05);
      nz(k, 'pink', t, 0.002, 0.42, 0.07, m, [['lowpass', 1800, 0.7], ['highpass', 220, 0.7]]);
      nz(k, 'white', t + 0.001, 0.0005, 0.16, 0.014, m, [['bandpass', 2300, 0.9]]);
      nz(k, 'white', t + 0.052, 0.002, 0.05, 0.03, m, [['bandpass', 1500, 1.2]]);
      return 0.32;
    },
    /* quill scratch: four strokes of band-passed noise with a rough fibre texture */
    pen: function (k) {
      var t = k.t, tex = G(k, 0.7), hp = BQ(k, 'highpass', 1600, 0.7);
      tex.connect(hp); hp.connect(k.o);
      var l = O(k, 'sawtooth', 43), lg = G(k, 0.3);
      l.connect(lg); lg.connect(tex.gain); l.start(t); l.stop(t + 0.6);
      var ST = [[0, 0.11, 3900, 1], [0.13, 0.085, 4600, 0.75], [0.245, 0.15, 3500, 0.95], [0.43, 0.07, 5200, 0.55]];
      for (var i = 0; i < ST.length; i++) {
        var s0 = t + ST[i][0], d = ST[i][1], f = ST[i][2], a = 0.3 * ST[i][3];
        var s = NS(k, 'white', s0, d + 0.02), bp = BQ(k, 'bandpass', f, 1.6), g = G(k, 0);
        bp.frequency.setValueAtTime(hz(k, f), s0); bp.frequency.linearRampToValueAtTime(hz(k, f * 1.18), s0 + d);
        g.gain.setValueAtTime(0, s0); g.gain.linearRampToValueAtTime(a, s0 + 0.012);
        g.gain.linearRampToValueAtTime(a * 0.8, s0 + d * 0.7); g.gain.linearRampToValueAtTime(0, s0 + d);
        s.connect(bp); bp.connect(g); g.connect(tex);
      }
      return 0.56;
    },
    /* page turn: rising swish, crinkle, soft flop */
    page: function (k) {
      var t = k.t, m = mixer(k, 0.06);
      var s = NS(k, 'pink', t, 0.4), bp = BQ(k, 'bandpass', 900, 0.9), g = G(k, 0);
      bp.frequency.setValueAtTime(hz(k, 900), t);
      bp.frequency.exponentialRampToValueAtTime(hz(k, 3800), t + 0.17);
      bp.frequency.exponentialRampToValueAtTime(hz(k, 1800), t + 0.34);
      g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.5, t + 0.12);
      g.gain.linearRampToValueAtTime(0.33, t + 0.2); g.gain.exponentialRampToValueAtTime(0.001, t + 0.36);
      s.connect(bp); bp.connect(g); g.connect(m);
      var s2 = NS(k, 'white', t + 0.03, 0.2), hp = BQ(k, 'highpass', 4200, 0.7), g2 = G(k, 0);
      [0.05, 0.095, 0.14].forEach(function (o) {
        var ti = t + o;
        g2.gain.setValueAtTime(0, ti); g2.gain.linearRampToValueAtTime(0.1, ti + 0.002); g2.gain.exponentialRampToValueAtTime(0.0005, ti + 0.02);
      });
      s2.connect(hp); hp.connect(g2); g2.connect(m);
      tone(k, 'sine', 170, t + 0.27, 0.003, 0.15, 0.08, m, 90, 0.05);
      nz(k, 'pink', t + 0.27, 0.002, 0.14, 0.05, m, [['lowpass', 700, 0.7]]);
      return 0.45;
    },
    /* loot sparkle: two struck bells with inharmonic partials and a little air */
    glint: function (k) { glintAt(k, k.t, 1.25, mixer(k, 0.35)); return 1.1; },
    /* item drop: soft pouch thump, then the sparkle */
    loot: function (k) {
      var t = k.t, m = mixer(k, 0.1);
      tone(k, 'sine', 120, t, 0.003, 0.26, 0.2, m, 55, 0.1);
      tone(k, 'triangle', 260, t, 0.002, 0.12, 0.06, m, 150, 0.05);
      nz(k, 'pink', t, 0.002, 0.24, 0.07, m, [['lowpass', 700, 0.7]]);
      glintAt(k, t + 0.06, 1.15, m);
      return 1.2;
    },
    /* scene transition air, panned left to right */
    whoosh: function (k) {
      var t = k.t, dest = k.o;
      if (k.c.createStereoPanner) {
        var pn = k.c.createStereoPanner();
        pn.pan.setValueAtTime(-0.6, t); pn.pan.linearRampToValueAtTime(0.6, t + 0.8);
        pn.connect(k.o); dest = pn;
      }
      var g = G(k, 0); g.connect(dest); k.send(g, 0.1);
      g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.95, t + 0.3); g.gain.exponentialRampToValueAtTime(0.001, t + 0.8);
      var s = NS(k, 'pink', t, 0.85), bp = BQ(k, 'bandpass', 260, 1.1);
      bp.frequency.setValueAtTime(hz(k, 260), t);
      bp.frequency.exponentialRampToValueAtTime(hz(k, 2200), t + 0.34);
      bp.frequency.exponentialRampToValueAtTime(hz(k, 520), t + 0.8);
      s.connect(bp); bp.connect(g);
      var s2 = NS(k, 'white', t, 0.85), bp2 = BQ(k, 'bandpass', 5500, 0.9), g2 = G(k, 0.12);
      s2.connect(bp2); bp2.connect(g2); g2.connect(g);
      return 0.85;
    },
    /* low thunder / tension swell, 1.5 s */
    rumble: function (k) {
      var t = k.t, m = mixer(k, 0.25), g = G(k, 0);
      g.connect(m);
      g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(1, t + 0.95); g.gain.setTargetAtTime(0, t + 0.95, 0.17);
      var s = NS(k, 'brown', t, 1.65), lp = BQ(k, 'lowpass', 140, 0.9), sg = G(k, 0.3);
      lp.frequency.setValueAtTime(hz(k, 140), t);
      lp.frequency.linearRampToValueAtTime(hz(k, 330), t + 0.95);
      lp.frequency.linearRampToValueAtTime(hz(k, 170), t + 1.55);
      s.connect(lp); lp.connect(sg); sg.connect(g);
      var o = O(k, 'sine', 44), v = O(k, 'sine', 4.2), vg = G(k, 2.5), og = G(k, 0.13);
      v.connect(vg); vg.connect(o.frequency); o.connect(og); og.connect(g);
      var tri = O(k, 'triangle', 88), tl = BQ(k, 'lowpass', 300, 0.7), tg = G(k, 0.1);
      tri.connect(tl); tl.connect(tg); tg.connect(g);
      [o, v, tri].forEach(function (x) { x.start(t); x.stop(t + 1.65); });
      nz(k, 'brown', t + 0.42, 0.06, 0.22, 0.35, m, [['bandpass', 420, 0.8]]);
      nz(k, 'brown', t + 0.76, 0.05, 0.28, 0.45, m, [['bandpass', 380, 0.8]]);
      return 1.7;
    },
    /* lub-dub, with enough upper body to be felt on a phone speaker */
    heartbeat: function (k) {
      var t = k.t, m = mixer(k, 0.05), lp = BQ(k, 'lowpass', 650, 0.7);
      lp.connect(m);
      function beat(tt, a) {
        tone(k, 'sine', 64, tt, 0.006, 0.42 * a, 0.17, m, 40, 0.12);
        tone(k, 'triangle', 140, tt, 0.006, 0.4 * a, 0.1, lp, 85, 0.08);
        nz(k, 'pink', tt, 0.004, 0.3 * a, 0.07, lp, [['lowpass', 550, 0.9]]);
        tone(k, 'sine', 270, tt, 0.004, 0.2 * a, 0.09, m, 170, 0.07);
      }
      beat(t, 1); beat(t + 0.2, 0.72);
      return 0.5;
    },
    /* glass / ice crack: a snap, a run of micro-fractures, cold pings */
    crack: function (k) {
      var t = k.t, m = mixer(k, 0.28);
      var s = NS(k, 'white', t, 0.32), hp = BQ(k, 'highpass', 2600, 0.7), g = G(k, 0);
      s.connect(hp); hp.connect(g); g.connect(m);
      var T = [0, 0.016, 0.037, 0.061, 0.094, 0.14, 0.19], A = [0.55, 0.38, 0.3, 0.24, 0.17, 0.1, 0.06], D = [0.014, 0.01, 0.012, 0.009, 0.014, 0.01, 0.012];
      var P = [4300, 6100, 3350, 5200];
      for (var i = 0; i < T.length; i++) {
        var ti = t + T[i];
        g.gain.setValueAtTime(0, ti); g.gain.linearRampToValueAtTime(A[i], ti + 0.0008); g.gain.exponentialRampToValueAtTime(0.0003, ti + D[i]);
        if (i < P.length) tone(k, 'sine', P[i], ti, 0.0006, 0.16 * A[i], 0.1 + i * 0.02, m);
      }
      tone(k, 'sine', 1873, t, 0.001, 0.035, 0.3, m);
      tone(k, 'sine', 2963, t + 0.016, 0.001, 0.025, 0.22, m);
      return 0.5;
    },
    /* weapon impact: transient + body, lightly saturated */
    hit: function (k) {
      var t = k.t, m = G(k, 1), sh = k.c.createWaveShaper(), post = G(k, 0.55);
      sh.curve = driveCurve();
      m.connect(sh); sh.connect(post); post.connect(k.o); k.send(post, 0.12);
      nz(k, 'white', t, 0.0008, 0.7, 0.045, m, [['bandpass', 2200, 0.7]]);
      tone(k, 'sine', 165, t, 0.002, 0.42, 0.2, m, 55, 0.12);
      tone(k, 'triangle', 400, t, 0.001, 0.4, 0.07, m, 140, 0.05);
      nz(k, 'pink', t, 0.002, 0.28, 0.1, m, [['lowpass', 1200, 0.7]]);
      return 0.36;
    },
    /* critical: bright rising D-major arpeggio, held top chord, sparkle */
    crit: function (k) {
      var t = k.t, m = mixer(k, 0.3), N = [587.33, 739.99, 880, 1174.66, 1479.98];
      for (var i = 0; i < N.length; i++) { var last = i === N.length - 1; brass(k, N[i], t + i * 0.058, last ? 0.13 : 0.105, last ? 0.65 : 0.2, m); }
      var tl = t + 4 * 0.058;
      brass(k, 1174.66, tl, 0.065, 0.65, m);
      brass(k, 880, tl, 0.06, 0.65, m);
      glintAt(k, tl + 0.02, 0.8, m);
      return 1.25;
    },
    /* fumble: gentle "wah wah wah waah" with a wobbling last note */
    fumble: function (k) {
      var t = k.t, m = mixer(k, 0.12), F = [233.08, 220, 207.65, 196], D = [0.24, 0.24, 0.24, 0.85];
      var o1 = O(k, 'sawtooth', F[0]), o2 = O(k, 'triangle', F[0]), a1 = G(k, 0.45), lp = BQ(k, 'lowpass', 450, 5), g = G(k, 0);
      o1.connect(a1); a1.connect(lp); o2.connect(lp); lp.connect(g); g.connect(m);
      g.gain.setValueAtTime(0, t);
      var tt = t;
      for (var i = 0; i < 4; i++) {
        var f = hz(k, F[i]);
        o1.frequency.setTargetAtTime(f, tt, 0.012); o2.frequency.setTargetAtTime(f, tt, 0.012);
        g.gain.setTargetAtTime(0.016, tt, 0.01); g.gain.setTargetAtTime(0.11, tt + 0.03, 0.02);
        lp.frequency.setTargetAtTime(hz(k, 380), tt, 0.01);
        lp.frequency.setTargetAtTime(hz(k, 1500), tt + 0.03, 0.05);
        lp.frequency.setTargetAtTime(hz(k, 520), tt + D[i] * 0.6, 0.06);
        tt += D[i];
      }
      var tl = t + 0.72, vib = O(k, 'sine', 5.5), vg = G(k, 0);
      vib.connect(vg); vg.connect(o1.frequency); vg.connect(o2.frequency);
      vg.gain.setValueAtTime(0, tl); vg.gain.linearRampToValueAtTime(hz(k, 196) * 0.03, tl + 0.3);
      g.gain.setTargetAtTime(0, tt - 0.22, 0.07);
      var end = tt + 0.2;
      [o1, o2, vib].forEach(function (x) { x.start(t); x.stop(end); });
      return end - t + 0.05;
    },
    /* card reveal: rising pad, shimmer, gathering sparkles, bloom (~1.4 s to the peak) */
    reveal: function (k) {
      var t = k.t, m = mixer(k, 0.45), lp = BQ(k, 'lowpass', 600, 0.8), pg = G(k, 0), i;
      lp.frequency.setValueAtTime(hz(k, 600), t);
      lp.frequency.exponentialRampToValueAtTime(hz(k, 4200), t + 1.2);
      lp.frequency.exponentialRampToValueAtTime(hz(k, 2400), t + 1.6);
      lp.connect(pg); pg.connect(m);
      pg.gain.setValueAtTime(0, t); pg.gain.linearRampToValueAtTime(0.25, t + 0.55);
      pg.gain.linearRampToValueAtTime(1, t + 1.18); pg.gain.setTargetAtTime(0, t + 1.22, 0.14);
      var C = [220, 329.63, 440, 554.37], V = [['triangle', 4], ['sine', -4]];
      for (i = 0; i < C.length; i++) {
        for (var j = 0; j < 2; j++) {
          var o = O(k, V[j][0], C[i]), og = G(k, 0.045);
          o.detune.value = V[j][1];
          o.frequency.setValueAtTime(hz(k, C[i]), t); o.frequency.exponentialRampToValueAtTime(hz(k, C[i] * 1.0595), t + 1.25);
          o.connect(og); og.connect(lp); o.start(t); o.stop(t + 2.0);
        }
      }
      var s = NS(k, 'white', t, 1.9), hp = BQ(k, 'highpass', 3000, 0.7), sg = G(k, 0);
      hp.frequency.setValueAtTime(hz(k, 3000), t); hp.frequency.exponentialRampToValueAtTime(hz(k, 9000), t + 1.2);
      sg.gain.setValueAtTime(0, t); sg.gain.linearRampToValueAtTime(0.05, t + 1.15); sg.gain.setTargetAtTime(0, t + 1.2, 0.12);
      s.connect(hp); hp.connect(sg); sg.connect(m);
      var ST = [0.5, 0.72, 0.88, 1.0, 1.09, 1.16, 1.22], SF = [1760, 2217.46, 2637.02, 2959.96, 3520, 2637.02, 4434.92];
      for (i = 0; i < ST.length; i++) bell(k, SF[i], t + ST[i], [[1, 1, 0.35], [2.76, 0.35, 0.2]], 0.022 + 0.004 * i, panned(k, m, i % 2 ? 0.45 : -0.45));
      glintAt(k, t + 1.2, 0.8, m);
      tone(k, 'sine', 110, t + 1.2, 0.01, 0.2, 0.5, m, 82, 0.4);
      return 2.0;
    },
    /* great hall doors: deep boom, wooden slab, iron bands, long tail */
    boom: function (k) {
      var t = k.t, m = mixer(k, 0.75), lp = BQ(k, 'lowpass', 520, 0.7);
      lp.connect(m);
      nz(k, 'brown', t, 0.004, 0.16, 1.7, m, [['lowpass', 230, 0.8]]);
      tone(k, 'sine', 58, t, 0.004, 0.17, 2.2, m, 33, 0.7);
      tone(k, 'triangle', 100, t, 0.003, 0.34, 0.9, lp, 58, 0.35);
      tone(k, 'triangle', 185, t, 0.002, 0.22, 0.5, lp, 110, 0.25);
      nz(k, 'pink', t, 0.001, 0.56, 0.16, m, [['bandpass', 650, 1.1]]);
      bell(k, 612, t + 0.015, [[1, 1, 0.5], [1.93, 0.6, 0.4], [3.1, 0.3, 0.25]], 0.06, m);
      nz(k, 'white', t + 0.09, 0.002, 0.05, 0.12, m, [['bandpass', 2600, 2]]);
      return 3.0;
    },
    /* warm F-major-9 swell with rising sparkles */
    heal: function (k) {
      var t = k.t, m = mixer(k, 0.45), lp = BQ(k, 'lowpass', 1200, 0.6), g = G(k, 0), i;
      lp.frequency.setValueAtTime(hz(k, 1200), t); lp.frequency.linearRampToValueAtTime(hz(k, 3200), t + 0.6);
      lp.connect(g); g.connect(m);
      g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(1, t + 0.38);
      g.gain.linearRampToValueAtTime(0.8, t + 0.9); g.gain.setTargetAtTime(0, t + 0.95, 0.25);
      var C = [87.31, 174.61, 261.63, 329.63, 392.0, 440.0];
      for (i = 0; i < C.length; i++) {
        var types = i === 0 ? ['sine'] : ['sine', 'triangle'];
        for (var j = 0; j < types.length; j++) {
          var o = O(k, types[j], C[i]), og = G(k, i === 0 ? 0.1 : 0.032);
          o.detune.value = j ? 5 : -5;
          o.connect(og); og.connect(lp); o.start(t); o.stop(t + 2.7);
        }
      }
      var SP = [1046.5, 1318.51, 1567.98, 1760];
      for (i = 0; i < SP.length; i++) bell(k, SP[i], t + 0.22 + i * 0.18, [[1, 1, 0.5], [2.76, 0.3, 0.25]], 0.02, panned(k, m, i % 2 ? 0.35 : -0.35));
      return 2.7;
    },
    /* coin clink: three small metal strikes */
    gold: function (k) {
      var t = k.t, m = mixer(k, 0.15), P = [[1, 1, 0.28], [1.34, 0.55, 0.2], [2.02, 0.42, 0.13], [2.95, 0.28, 0.09]];
      function coin(tt, f, a, pan) {
        var d = panned(k, m, pan);
        bell(k, f, tt, P, 0.1 * a, d);
        nz(k, 'white', tt, 0.0005, 0.1 * a, 0.006, d, [['highpass', 5000, 0.7]]);
      }
      coin(t, 2637.02, 1, -0.15); coin(t + 0.075, 3050, 0.7, 0.2); coin(t + 0.135, 2790, 0.45, 0);
      return 0.6;
    }
  };

  /* ------------------------------------------------------------------ beds (looping ambiences) */

  function bedNoise(k, kind) {
    var s = k.c.createBufferSource(), b = noiseBuf(k.c, kind);
    s.buffer = b; s.loop = true; s.start(k.t, k.rnd() * b.duration * 0.9);
    k.srcs.push(s);
    return s;
  }
  function bedBuf(k, buf, gain, dest, rate) {
    var s = k.c.createBufferSource(), g = G(k, gain);
    s.buffer = buf; s.loop = true;
    if (rate) s.playbackRate.value = rate;
    s.connect(g); g.connect(dest);
    s.start(k.t, k.rnd() * buf.duration * 0.9);
    k.srcs.push(s);
    return g;
  }
  /* A random slow envelope with no timers: the shared white-noise table read a few
     samples per second (linear interpolation between samples) into an AudioParam. */
  function nenv(k, perSec, depth, param, shaper) {
    var s = k.c.createBufferSource(), b = noiseBuf(k.c, 'white'), node = s;
    s.buffer = b; s.loop = true;
    s.playbackRate.value = Math.max(1e-5, perSec / b.sampleRate);
    if (shaper) { var w = k.c.createWaveShaper(); w.curve = shaper; s.connect(w); node = w; }
    var g = G(k, depth);
    node.connect(g); g.connect(param);
    s.start(k.t, k.rnd() * b.duration * 0.95);
    k.srcs.push(s);
    return g;
  }
  function bedOsc(k, type, f) { var o = O(k, type, f); o.start(k.t); k.srcs.push(o); return o; }
  function crowdClink(k, tt) {
    var r = k.rnd, g = G(k, 1);
    g.connect(panned(k, k.o, (r() * 2 - 1) * 0.7));
    k.send(g, 0.35);
    if (r() < 0.72) {
      var f = [1850, 2330, 2750, 2100][Math.floor(r() * 4)];
      bell(k, f, tt, [[1, 1, 0.22], [2.4, 0.5, 0.14], [3.9, 0.3, 0.09]], 0.028 + r() * 0.02, g);
      if (r() < 0.55) bell(k, f * (1.04 + r() * 0.1), tt + 0.05 + r() * 0.05, [[1, 1, 0.2], [2.4, 0.45, 0.12]], 0.02, g);
    } else {
      tone(k, 'sine', 190, tt, 0.004, 0.07, 0.09, g, 130, 0.06);
      nz(k, 'pink', tt, 0.003, 0.05, 0.05, g, [['lowpass', 900, 0.7]]);
    }
  }

  var BEDS = {
    /* low tavern murmur: speech-band noise with syllable-rate swells, two muffled
       voices, and the odd mug clink scheduled a couple of seconds ahead */
    crowd: function (k) {
      var mix = G(k, 1), lp = BQ(k, 'lowpass', 2300, 0.5), sw = G(k, 0.78), i;
      mix.connect(lp); lp.connect(sw); sw.connect(k.o);
      nenv(k, 0.12, 0.2, sw.gain);
      var src = bedNoise(k, 'pink'), B = [[270, 1.3, 0.9, 3.3], [540, 1.7, 1, 4.4], [1000, 2.1, 0.75, 5.1]];
      for (i = 0; i < B.length; i++) {
        var bp = BQ(k, 'bandpass', B[i][0], B[i][1]), am = G(k, B[i][2] * 0.55);
        src.connect(bp); bp.connect(am); am.connect(mix);
        nenv(k, B[i][3], B[i][2] * 0.45, am.gain);
      }
      var V = [[128, 1], [196, 0.75]];
      for (i = 0; i < V.length; i++) {
        var o = bedOsc(k, 'sawtooth', V[i][0]), f1 = BQ(k, 'bandpass', 620, 4), am2 = G(k, 0), vg = G(k, 0.05 * V[i][1]);
        nenv(k, 1.3, V[i][0] * 0.12, o.frequency);
        nenv(k, 2.6, 200, f1.frequency);
        nenv(k, 4.6 + i, 1, am2.gain, reluCurve());
        o.connect(f1); f1.connect(am2); am2.connect(vg); vg.connect(mix);
      }
      k.gap = function (r) { return 1.8 + r() * 5.5; };
      k.sched = function (tt) { crowdClink(k, tt); };
    },
    /* rain: hiss + body with a slow intensity drift, two drop tables of different lengths */
    rain: function (k) {
      var inten = G(k, 0.85);
      inten.connect(k.o);
      nenv(k, 0.22, 0.15, inten.gain);
      var h = bedNoise(k, 'white'), hp = BQ(k, 'highpass', 1100, 0.6), lp = BQ(k, 'lowpass', 8000, 0.5), hg = G(k, 0.13);
      h.connect(hp); hp.connect(lp); lp.connect(hg); hg.connect(inten);
      var b = bedNoise(k, 'pink'), blp = BQ(k, 'lowpass', 1500, 0.6), bhp = BQ(k, 'highpass', 170, 0.6), bg = G(k, 0.35);
      b.connect(blp); blp.connect(bhp); bhp.connect(bg); bg.connect(inten);
      bedBuf(k, sparse(k.c, 'drops1'), 0.6, k.o);
      bedBuf(k, sparse(k.c, 'drops2'), 0.45, k.o, 0.97);
    },
    /* hearth: flickering low roar, breath, faint hiss, two crackle tables (37 s before the pattern repeats) */
    fire: function (k) {
      var roar = bedNoise(k, 'brown'), rlp = BQ(k, 'lowpass', 380, 0.7), ram = G(k, 0.3);
      roar.connect(rlp); rlp.connect(ram); ram.connect(k.o);
      nenv(k, 3.2, 0.12, ram.gain);
      var br = bedNoise(k, 'pink'), bbp = BQ(k, 'bandpass', 800, 0.6), bam = G(k, 0.05);
      br.connect(bbp); bbp.connect(bam); bam.connect(k.o);
      nenv(k, 0.9, 0.03, bam.gain);
      var hs = bedNoise(k, 'white'), hhp = BQ(k, 'highpass', 5000, 0.7), hg = G(k, 0.012);
      hs.connect(hhp); hhp.connect(hg); hg.connect(k.o);
      var chp = BQ(k, 'highpass', 250, 0.7);
      chp.connect(k.o);
      bedBuf(k, sparse(k.c, 'crack1'), 1.1, chp);
      bedBuf(k, sparse(k.c, 'crack2'), 0.85, chp);
    }
  };

  /* ------------------------------------------------------------------ audio context */

  function makeCtx() {
    if (S.ctx && S.ctx.state !== 'closed') return S.ctx;
    var AC = win.AudioContext || win.webkitAudioContext;
    if (!AC) return null;
    var c = null;
    /* The page's static Begin button may have opened a context inside the player's tap, before this file loaded. */
    if (win.__qoAC && win.__qoAC.state !== 'closed') { c = win.__qoAC; win.__qoAC = null; }
    else { try { c = new AC({ latencyHint: 'interactive' }); } catch (e) { try { c = new AC(); } catch (e2) { return null; } } }
    S.ctx = c;
    S.bus = buildBus(c);
    S.bus.music = c.createGain(); S.bus.music.connect(c.destination);
    S.bus.files = c.createGain(); S.bus.files.connect(c.destination);
    S.bus.out.gain.value = S.muted ? 0 : 1;
    try { c.onstatechange = onCtxState; } catch (e) {}
    return c;
  }
  function playSilent(c) {
    try { var b = c.createBuffer(1, 1, c.sampleRate), s = c.createBufferSource(); s.buffer = b; s.connect(c.destination); s.start(0); } catch (e) {}
  }
  function resumeCtx() {
    var c = S.ctx;
    if (!c || c.state === 'running' || c.state === 'closed') return;
    try { var p = c.resume(); if (p && p.catch) p.catch(noop); } catch (e) {}
  }
  function suspendCtx() {
    var c = S.ctx;
    if (!c || c.state !== 'running') return;
    try { var p = c.suspend(); if (p && p.catch) p.catch(noop); } catch (e) {}
  }
  function anyBed() { for (var n in S.beds) if (has(S.beds, n)) return true; return false; }
  function onCtxState() {
    var c = S.ctx;
    if (!c || c.state === 'running' || c.state === 'closed') return;
    if (!S.unlocked || S.hidden || S.muted || S.destroyed) return;
    if (anyBed() || (S.route && S.cur)) later(resumeCtx, 250);
  }
  /* A silent running context still keeps the audio device awake; suspend after 40 s of quiet. */
  function touchCtx() {
    S.lastAct = now();
    resumeCtx();
    if (!S.idleTimer && !S.route) S.idleTimer = later(idleCheck, 15000);
  }
  function idleCheck() {
    S.idleTimer = 0;
    if (!S.ctx || S.destroyed || S.route) return;
    if (anyBed() || S.voices > 0 || now() - S.lastAct < 40000) { S.idleTimer = later(idleCheck, 15000); return; }
    suspendCtx();
  }
  function warmUp(c) {
    ['white', 'pink', 'brown'].forEach(function (kd) {
      var e = noiseEntry(c, kd);
      if (!e.done && !e.queued) { e.queued = true; queueJob(stepper(e, 16384)); }
    });
    [440, 659.25].forEach(function (f) { queueJob(function () { pluckBuf(c, f); return true; }); });
    var ir = irEntry(c);
    if (!ir.queued) {
      ir.queued = true;
      queueJob(stepper(ir, 12000));
      /* Handing the impulse to the convolver costs a few ms of FFT set-up on the main
         thread; keep it clear of the Begin transition. Until then effects play dry. */
      queueJob(function () {
        later(function () {
          idle(function () {
            if (S.ctx !== c || !S.bus || S.bus.revReady) return;
            try { S.bus.conv.buffer = ir.buf; S.bus.revReady = true; } catch (e) { report(e); }
          });
        }, 1800);
        return true;
      });
    } else if (ir.done && S.ctx === c && S.bus && !S.bus.revReady) {
      try { S.bus.conv.buffer = ir.buf; S.bus.revReady = true; } catch (e) { report(e); }
    }
  }

  /* ------------------------------------------------------------------ media elements */

  function volumeWritable() {
    try { var a = doc.createElement('audio'); a.volume = 0.5; return Math.abs(a.volume - 0.5) < 0.01; } catch (e) { return true; }
  }
  function playEl(el, onBlocked) {
    var p;
    try { p = el.play(); } catch (e) { if (onBlocked) onBlocked(e); return; }
    if (p && typeof p.then === 'function') {
      p.then(null, function (err) {
        if (!err || err.name !== 'NotAllowedError') return;
        el._qoBlessed = false;
        S.needBless = true;
        if (onBlocked) onBlocked(err);
      });
    }
  }
  /* iOS unblocks media elements one by one, and only inside a user gesture. A muted
     play + pause inside the gesture is enough; preload stays 'none' so this costs no data. */
  function bless(el) {
    el._qoBlessed = true;
    if (!el.paused) return;
    try {
      var m = el.muted;
      el.muted = true;
      var p = el.play();
      if (p && p.catch) p.catch(function (err) { if (err && err.name === 'NotAllowedError') { el._qoBlessed = false; S.needBless = true; } });
      el.pause();
      try { el.currentTime = 0; } catch (e) {}
      el.muted = m;
    } catch (e) {}
  }
  function blessAll() {
    if (!S.inGesture) { S.needBless = true; return; }
    S.needBless = false;
    var k, i;
    for (k in S.tracks) { var t = S.tracks[k]; if (t.el && !t.el._qoBlessed) bless(t.el); }
    for (k in S.pools) {
      var p = S.pools[k];
      for (i = 0; i < p.els.length; i++) if (p.els[i] && p.els[i].el && !p.els[i].el._qoBlessed) bless(p.els[i].el);
    }
  }
  /* WebKit: readies one more track inside this tap, making its element if needed. A track the quest already wants or
     prefetched comes first, then the tracks it plays next from the current one, then the rest. */
  function unblessed(k) { var t = has(MUSIC, k) ? track(k) : null; return t && !(t.el && t.el._qoBlessed) ? t : null; }
  function blessNext() {
    if (!S.gate || !S.inGesture || S.destroyed) return false;
    var pick = null, k, t, n;
    for (k in S.tracks) {
      t = S.tracks[k];
      if (!pick && (t.target === 1 || t.waiting || t.needsStart || t.preload === 'auto') && !(t.el && t.el._qoBlessed)) pick = t;
    }
    for (k = S.cur ? NEXT_MUSIC[S.cur] : BLESS_ORDER[0], n = 0; !pick && k && n < 8; k = NEXT_MUSIC[k], n++) pick = unblessed(k);
    for (n = 0; !pick && n < BLESS_ORDER.length; n++) pick = unblessed(BLESS_ORDER[n]);
    for (k in MUSIC) if (!pick && has(MUSIC, k)) pick = unblessed(k);
    if (!pick) return false;
    var el = ensureEl(pick);
    if (el && !el._qoBlessed) bless(el);
    return true;
  }
  function routeEl(el, holder, busNode, plain) {
    if (!S.route || !S.ctx || plain) return false;
    try {
      holder.node = S.ctx.createMediaElementSource(el);
      holder.gain = S.ctx.createGain();
      holder.gain.gain.value = 0;
      holder.node.connect(holder.gain);
      holder.gain.connect(busNode);
      holder.routed = true;
      return true;
    } catch (e) { holder.node = holder.gain = null; holder.routed = false; return false; }
  }
  function newAudio(url, preload, loop, plain) {
    var el = doc.createElement('audio');
    if (S.route && !plain) { try { el.crossOrigin = 'anonymous'; } catch (e) {} }
    el.preload = preload || 'none';
    el.loop = !!loop;
    el._qoBlessed = !S.gate;
    el.src = url;
    return el;
  }

  /* ---- music tracks */

  function track(key) {
    if (S.tracks[key]) return S.tracks[key];
    if (!has(MUSIC, key)) return null;
    var t = {
      key: key, url: MUSIC[key], el: null, node: null, gain: null, routed: false,
      phi: 0, target: 0, fade: cfg.fade, v: -1, waiting: false, cancelWait: null,
      needsStart: false, preload: 'none', plain: false
    };
    S.tracks[key] = t;
    return t;
  }
  function ensureEl(t) {
    if (t.el) return t.el;
    var el;
    try { el = newAudio(t.url, t.preload, true, t.plain); } catch (e) { return null; }
    t.routed = false; t.node = t.gain = null; t.v = -1;
    if (!routeEl(el, t, S.bus && S.bus.music, t.plain)) { try { el.volume = 0; } catch (e) {} }
    el.addEventListener('error', function () { onMediaError(t, el); });
    t.el = el;
    S.needBless = true;
    return el;
  }
  function dropTrackEl(t) {
    if (t.cancelWait) t.cancelWait();
    var el = t.el;
    t.el = null;
    if (el) { try { el.pause(); el.removeAttribute('src'); el.load(); } catch (e) {} }
    try { if (t.gain) t.gain.disconnect(); if (t.node) t.node.disconnect(); } catch (e) {}
    t.node = t.gain = null; t.routed = false; t.v = -1;
  }
  function onMediaError(t, el) {
    if (t.el !== el || S.destroyed) return;
    var code = el.error && el.error.code;
    if (t.routed && code === 4 && !t.plain) {
      /* Could be CORS, could be a missing file: retry just this track as a plain element. */
      t.plain = true;
      var wasCur = S.cur === t.key;
      dropTrackEl(t);
      ensureEl(t);
      if (wasCur && canPlayMusic()) { t.phi = 0; t.target = 1; startTrack(t, true); waitThenCross(t); }
      return;
    }
    warnOnce('err:' + t.key, 'could not load music "' + t.key + '"');
    if (t.waiting && t.cancelWait) {
      t.cancelWait(); t.target = 0; t.phi = 0; haltTrack(t);
      if (S.cur === t.key) {
        S.cur = null;
        for (var k in S.tracks) if (S.tracks[k].target === 1 && S.tracks[k].el && !S.tracks[k].el.paused) S.cur = k;
      }
    }
  }
  function haltTrack(t) {
    if (t.el && !t.el.paused) { try { t.el.pause(); } catch (e) {} }
  }
  function setTrackVol(t, v) {
    v = clamp(v, 0, 1);
    if (Math.abs(v - t.v) < 0.0001 && !(v === 0 && t.v !== 0)) return;
    t.v = v;
    if (t.gain && S.ctx) { try { t.gain.gain.setTargetAtTime(v, S.ctx.currentTime, 0.012); } catch (e) {} }
    else if (t.el) { try { t.el.volume = v; } catch (e) {} }
  }
  function startTrack(t, restart) {
    if (t.el && t.el.error) dropTrackEl(t);
    var el = ensureEl(t);
    if (!el) return;
    t.needsStart = false;
    if (restart) { try { el.currentTime = 0; } catch (e) {} }
    if (el.preload !== 'auto') el.preload = 'auto';
    if (t.routed) resumeCtx();
    playEl(el, function () { S.needBless = true; });
  }
  /* Hold the crossfade until the new track is actually sounding, so a slow first
     download never pops in mid-fade. The old track keeps playing meanwhile. */
  function waitThenCross(t) {
    if (t.cancelWait) t.cancelWait();
    var el = t.el;
    if (!el) return;
    t.waiting = true;
    var done = false, timer = 0, polls = 0;
    function cleanup() {
      done = true; t.waiting = false; t.cancelWait = null;
      clearTimeout(timer);
      try { el.removeEventListener('playing', onPlaying); } catch (e) {}
    }
    function onPlaying() {
      if (done) return;
      cleanup();
      if (S.cur === t.key && t.el === el) { crossTo(t); if (t.routed) healthCheck(t); }
    }
    function onTimer() {
      if (done) return;
      if (!el.paused) { onPlaying(); return; }
      if (++polls < 60) timer = setTimeout(onTimer, 1000);
    }
    try { el.addEventListener('playing', onPlaying); } catch (e) {}
    timer = setTimeout(onTimer, 4000);
    t.cancelWait = function () { if (!done) cleanup(); };
  }
  function crossTo(t) {
    var instant = t.fade <= 0 || (S.volFixed && !t.routed);
    for (var k in S.tracks) {
      var o = S.tracks[k];
      if (o === t) continue;
      if (o.waiting && o.cancelWait) o.cancelWait();
      o.target = 0; o.fade = t.fade;
      if (instant) { o.phi = 0; haltTrack(o); setTrackVol(o, 0); }
    }
    t.target = 1;
    if (instant) t.phi = 1;
    kick();
  }
  function canPlayMusic() { return S.unlocked && !S.muted && !S.hidden && !S.destroyed; }
  function resumeCurrent(fade) {
    var t = S.cur && S.tracks[S.cur];
    if (!t) return;
    for (var k in S.tracks) {
      var o = S.tracks[k];
      if (o !== t && (!o.el || o.el.paused)) { o.phi = 0; o.target = 0; }
    }
    if (t.waiting) { if (t.el && t.el.paused && t.cancelWait) t.cancelWait(); else return; }
    if (t.el && !t.el.paused) { t.target = 1; kick(); return; }
    t.fade = fade; t.phi = 0; t.target = 1;
    startTrack(t, !!t.needsStart);
    waitThenCross(t);
    kick();
  }
  function music(key, o) {
    o = o || {};
    var fade = Math.max(0, num(o.fade, cfg.fade)), k, x;
    if (key == null || key === '' || key === false) {
      S.cur = null;
      for (k in S.tracks) {
        x = S.tracks[k];
        if (x.waiting && x.cancelWait) { x.cancelWait(); x.phi = 0; haltTrack(x); }
        x.target = 0; x.fade = fade; x.needsStart = false;
        if (fade <= 0) { x.phi = 0; haltTrack(x); }
      }
      kick();
      return true;
    }
    key = String(key);
    var t = track(key);
    if (!t) { warnOnce('m:' + key, 'unknown music key "' + key + '"'); return false; }
    var same = S.cur === key;
    S.cur = key;
    t.fade = fade;
    for (k in S.tracks) {
      x = S.tracks[k];
      if (x !== t && x.waiting && x.cancelWait) { x.cancelWait(); x.target = 0; x.phi = 0; haltTrack(x); }
    }
    if (!canPlayMusic()) {
      /* Locked, muted or hidden: remember the choice; unlock / unmute / show starts it. */
      for (k in S.tracks) { x = S.tracks[k]; if (x !== t) { x.target = 0; x.phi = 0; haltTrack(x); } }
      if (!same || o.restart) t.needsStart = true;
      t.phi = 0; t.target = 1;
      if (t.waiting && t.cancelWait) t.cancelWait();
      haltTrack(t);
      kick();
      return true;
    }
    if (same && !o.restart) {
      if (t.waiting) return true;
      if (t.el && !t.el.paused) { if (t.target !== 1) crossTo(t); return true; }
      resumeCurrent(fade);
      return true;
    }
    if (!o.restart && t.el && !t.el.paused && t.phi > 0) { crossTo(t); return true; }
    startTrack(t, true);
    waitThenCross(t);
    kick();
    return true;
  }

  /* Routed audio should never be silent. If WebKit ever hands back zeros for a
     cross-origin element, switch to plain elements (re-blessed on the next tap). */
  function healthCheck(t) {
    if (S.health || !t.node || !S.ctx || !S.route) return;
    S.health = 'checking';
    var c = S.ctx, an, sink;
    try {
      an = c.createAnalyser(); an.fftSize = 1024;
      sink = c.createGain(); sink.gain.value = 0;
      t.node.connect(an); an.connect(sink); sink.connect(c.destination);
    } catch (e) { S.health = 'skipped'; return; }
    var fl = !!an.getFloatTimeDomainData, buf = fl ? new Float32Array(an.fftSize) : new Uint8Array(an.fftSize);
    var reads = 0, waits = 0;
    function finish(st) {
      S.health = st;
      try { t.node.disconnect(an); } catch (e) {}
      try { an.disconnect(); sink.disconnect(); } catch (e) {}
      if (st === 'silent') fallbackPlain('silent output');
    }
    function probe() {
      if (S.destroyed || !S.route) return;
      if (!t.el || t.el.paused || S.hidden || c.state !== 'running') { if (++waits < 40) later(probe, 500); else finish('skipped'); return; }
      reads++;
      var i, heard = false;
      if (fl) { an.getFloatTimeDomainData(buf); for (i = 0; i < buf.length; i++) if (buf[i] > 1e-5 || buf[i] < -1e-5) { heard = true; break; } }
      else { an.getByteTimeDomainData(buf); for (i = 0; i < buf.length; i++) if (buf[i] !== 128) { heard = true; break; } }
      if (heard) { finish('ok'); return; }
      if (reads >= 16 && t.el.currentTime > 3) { finish('silent'); return; }
      later(probe, 250);
    }
    later(probe, 1500);
  }
  function fallbackPlain(why) {
    if (!S.route) return;
    S.route = false;
    S.health = 'plain (' + why + ')';
    warnOnce('route', 'Web Audio routing disabled (' + why + '); using plain audio elements');
    var k, i;
    for (k in S.tracks) {
      var t = S.tracks[k];
      if (!t.routed) continue;
      var wasOn = !!(t.el && !t.el.paused);
      dropTrackEl(t);
      if (!S.gate || S.cur === k) ensureEl(t);
      if (wasOn && S.cur === k && canPlayMusic()) { t.phi = 1; t.target = 1; startTrack(t, true); }
    }
    for (k in S.pools) {
      var p = S.pools[k];
      for (i = 0; i < p.els.length; i++) if (p.els[i] && p.els[i].routed) { dropFileEl(p, i); if (!S.gate) ensureFileEl(p, i); }
    }
    S.needBless = true;
    kick();
  }

  /* ---- file effects: two pooled elements per key so quick repeats overlap */

  function pool(key) {
    if (S.pools[key]) return S.pools[key];
    if (!has(FILES, key)) return null;
    S.pools[key] = { key: key, url: FILES[key], els: [], idx: 0, preload: WARM[key] ? 'auto' : 'none', plain: false };
    return S.pools[key];
  }
  function ensureFileEl(p, i) {
    var e = p.els[i];
    if (e && e.el) return e;
    var el;
    try { el = newAudio(p.url, i === 0 ? p.preload : 'none', false, p.plain); } catch (x) { return null; }
    e = { el: el, node: null, gain: null, routed: false };
    routeEl(el, e, S.bus && S.bus.files, p.plain);
    el.addEventListener('error', function () {
      if (p.els[i] !== e || S.destroyed) return;
      if (e.routed && !p.plain && el.error && el.error.code === 4) {
        p.plain = true;
        for (var j = 0; j < 2; j++) { dropFileEl(p, j); ensureFileEl(p, j); }
      } else warnOnce('ferr:' + p.key, 'could not load effect "' + p.key + '"');
    });
    p.els[i] = e;
    S.needBless = true;
    return e;
  }
  function dropFileEl(p, i) {
    var e = p.els[i];
    if (!e) return;
    p.els[i] = null;
    try { e.el.pause(); e.el.removeAttribute('src'); e.el.load(); } catch (x) {}
    try { if (e.gain) e.gain.disconnect(); if (e.node) e.node.disconnect(); } catch (x) {}
  }
  /* A prefetched effect from memory: a buffer source into the files bus, at the effect's own level. On WebKit every
     effect plays this way once its buffer is in (see BUF_ORDER). */
  function fromBuf(key, vol, rate) {
    var b = (BUF[key] || S.gate) && S.bufs && S.bufs[key];
    if (!b || !b.buf || !S.ctx || !S.bus || !S.bus.files || !canSynth()) return false;
    try {
      touchCtx();
      var c = S.ctx, src = c.createBufferSource(), g = c.createGain();
      src.buffer = b.buf; src.playbackRate.value = rate; g.gain.value = vol;
      src.connect(g); g.connect(S.bus.files);
      /* Kept until it ends, so mute and leaving the page stop it at once, like an element. */
      S.srcs.push(src);
      src.onended = function () { var i = S.srcs.indexOf(src); if (i > -1) S.srcs.splice(i, 1); try { g.disconnect(); src.disconnect(); } catch (e) {} };
      src.start(c.currentTime + 0.005);
      return true;
    } catch (e) { report(e); return false; }
  }
  /* Resolves when the buffer is in or has failed (never rejects), so the WebKit warm-up can load one file at a time. */
  function loadBuf(key) {
    if (!(BUF[key] || S.gate) || !has(FILES, key) || !S.ctx || !win.fetch) return Promise.resolve();
    S.bufs = S.bufs || {};
    if (S.bufs[key]) return S.bufs[key].p || Promise.resolve();
    var c = S.ctx, b = S.bufs[key] = { buf: null, p: null };
    try {
      b.p = win.fetch(FILES[key]).then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.arrayBuffer(); }).then(function (ab) {
        return new Promise(function (res, rej) { var q = c.decodeAudioData(ab, res, rej); if (q && q.then) q.then(res, rej); });
      }).then(function (buf) { b.buf = buf; }, function () { if (S.bufs && S.bufs[key] === b) delete S.bufs[key]; warnOnce('buf:' + key, 'effect "' + key + '" plays from its element'); });
    } catch (e) { delete S.bufs[key]; return Promise.resolve(); }
    return b.p;
  }
  /* WebKit: every effect into memory after the unlock, one file at a time in the order the quest first plays them. */
  function warmBufs() {
    if (!S.gate || S.bufsWarm || S.destroyed || !S.ctx) return;
    S.bufsWarm = true;
    var keys = BUF_ORDER.filter(function (k) { return has(FILES, k); }), i = 0, k;
    for (k in FILES) if (has(FILES, k) && keys.indexOf(k) < 0) keys.push(k);
    (function next() {
      if (S.destroyed || i >= keys.length) return;
      loadBuf(keys[i++]).then(next, next);
    })();
  }
  function pauseFiles() {
    for (var k in S.pools) {
      var p = S.pools[k];
      for (var i = 0; i < p.els.length; i++) if (p.els[i] && p.els[i].el && !p.els[i].el.paused) { try { p.els[i].el.pause(); } catch (e) {} }
    }
    var all = S.srcs.splice(0);
    for (var j = 0; j < all.length; j++) { try { all[j].stop(); } catch (e) {} }
  }
  function playFile(key, o) {
    if (!S.unlocked || S.muted || S.hidden || S.destroyed) return false;
    var p = pool(key);
    if (!p) return false;
    var vol = clamp((has(FILE_VOL, key) ? FILE_VOL[key] : 0.6) * clamp(num(o.volume, 1), 0, 4) * cfg.sfxVolume, 0, 1);
    if (vol <= 0) return false;
    var rate = clamp(num(o.rate, 1), 0.25, 4), delay = Math.max(0, num(o.delay, 0));
    function fire() {
      if (S.muted || S.hidden || S.destroyed) return;
      if (fromBuf(key, vol, rate)) return;
      var i = p.idx;
      p.idx = (i + 1) % 2;
      var e = ensureFileEl(p, i);
      if (!e) return;
      var el = e.el, keep = rate === 1;
      try { el.playbackRate = rate; el.preservesPitch = keep; el.webkitPreservesPitch = keep; el.mozPreservesPitch = keep; } catch (x) {}
      if (e.gain && S.ctx) { resumeCtx(); try { e.gain.gain.setValueAtTime(vol, S.ctx.currentTime); } catch (x) {} }
      else { try { el.volume = vol; } catch (x) {} }
      try { el.currentTime = 0; } catch (x) {}
      if (el.preload !== 'auto') el.preload = 'auto';
      playEl(el, function () {
        S.needBless = true;
        try { var fb = new win.Audio(p.url); fb.volume = vol; var q = fb.play(); if (q && q.catch) q.catch(noop); } catch (x) {}
      });
    }
    if (delay > 0) later(fire, delay); else fire();
    var dk = o.duck != null ? num(o.duck, 0) : (AUTO_DUCK[key] || 0);
    if (dk > 0) { if (delay > 0) later(function () { duck(dk); }, delay); else duck(dk); }
    return true;
  }

  /* ------------------------------------------------------------------ fade engine
     One tiny ticker drives every music level: crossfade phase (equal power:
     level = sin(phi * PI/2)), the duck dip and the mute fade. It runs on
     requestAnimationFrame only while something is moving, with a 120 ms
     timer as a watchdog when frames are throttled. */

  var tk = { on: false, raf: 0, to: 0, last: 0, hold: 0 };
  function kick() {
    if (tk.on || S.destroyed) return;
    tk.on = true;
    tk.last = now();
    arm();
  }
  function arm() {
    if (win.requestAnimationFrame && !S.hidden) tk.raf = win.requestAnimationFrame(run);
    tk.to = setTimeout(run, 120);
  }
  function run() {
    if (!tk.on) return;
    if (tk.raf && win.cancelAnimationFrame) win.cancelAnimationFrame(tk.raf);
    tk.raf = 0;
    clearTimeout(tk.to); tk.to = 0;
    var active = false;
    try { active = update(); } catch (e) { report(e); }
    if (active && !S.destroyed) arm(); else tk.on = false;
  }
  function update() {
    var tn = now(), dt = Math.max(0, tn - tk.last), active = false, k, t;
    tk.last = tn;
    var mT = S.muted ? 0 : 1;
    if (S.muteM !== mT) {
      S.muteM = stepTo(S.muteM, mT, dt / (mT ? 450 : 200));
      if (S.muteM !== mT) active = true;
      else if (!mT) pauseAllMusic();
    }
    var dT = tn < S.duckUntil ? 1 : 0;
    if (S.duckD !== dT) {
      S.duckD = stepTo(S.duckD, dT, dt / (dT ? 140 : 700));
      if (S.duckD !== dT) active = true;
    }
    if (dT && S.duckD === 1) { clearTimeout(tk.hold); tk.hold = setTimeout(kick, S.duckUntil - tn + 10); }
    var duckMul = 1 - cfg.duckDepth * smooth(S.duckD);
    for (k in S.tracks) {
      t = S.tracks[k];
      if (!t.waiting && t.phi !== t.target) {
        t.phi = t.fade > 0 ? stepTo(t.phi, t.target, dt / t.fade) : t.target;
        if (t.phi !== t.target) active = true;
        else if (t.phi === 0) haltTrack(t);
      }
      if (t.el) setTrackVol(t, cfg.musicVolume * Math.sin(t.phi * PI / 2) * duckMul * S.muteM);
    }
    return active;
  }
  function pauseAllMusic() {
    for (var k in S.tracks) {
      var t = S.tracks[k];
      if (t.waiting && t.cancelWait) { t.cancelWait(); t.phi = 0; }
      if (t.target === 0) t.phi = 0;
      haltTrack(t);
    }
  }
  function duck(ms) {
    ms = clamp(num(ms, 1400), 0, 15000);
    S.duckUntil = Math.max(S.duckUntil, now() + ms);
    kick();
    return true;
  }
  function unduck() { S.duckUntil = 0; clearTimeout(tk.hold); kick(); return true; }

  /* ------------------------------------------------------------------ synth + beds playback */

  function canSynth() { return S.unlocked && !S.muted && !S.hidden && !S.destroyed && !!S.ctx; }
  function playSynth(name, o) {
    if (!canSynth()) return false;
    var tn = now();
    if (S.last[name] && tn - S.last[name] < 30) return false;
    if (S.voices >= 40 || (S.voices >= 24 && LOW_PRI[name])) return false;
    var vol = clamp(num(o.volume, 1), 0, 4) * cfg.sfxVolume;
    if (vol <= 0) return false;
    S.last[name] = tn;
    touchCtx();
    var c = S.ctx, out = c.createGain(), delay = Math.max(0, num(o.delay, 0)) / 1000;
    out.gain.value = vol;
    var pan = num(o.pan, 0);
    if (pan && c.createStereoPanner) { var p = c.createStereoPanner(); p.pan.value = clamp(pan, -1, 1); out.connect(p); p.connect(S.bus.sfx); }
    else out.connect(S.bus.sfx);
    var k = mkK(c, out, c.currentTime + 0.006 + delay, o, name, S.bus.revReady ? S.bus.revIn : null);
    var dur = SYN[name](k) || 1;
    S.voices++;
    later(function () { S.voices = Math.max(0, S.voices - 1); try { out.disconnect(); } catch (e) {} }, (delay + dur + 0.6) * 1000);
    var dk = o.duck != null ? num(o.duck, 0) : (AUTO_DUCK[name] || 0);
    if (dk > 0) { if (delay > 0) later(function () { duck(dk); }, delay * 1000); else duck(dk); }
    return true;
  }
  function bed(name, on, o) {
    o = o || {};
    name = String(name || '');
    if (!has(BEDS, name)) { warnOnce('bed:' + name, 'unknown bed "' + name + '"'); return false; }
    if (on === false || on === 0) {
      delete S.want[name];
      stopBed(name, Math.max(0, num(o.fade, 1200)));
      return true;
    }
    S.want[name] = { volume: clamp(num(o.volume, 1), 0, 2), fade: Math.max(0, num(o.fade, 1600)) };
    if (canSynth()) startBed(name);
    return true;
  }
  function startBed(name) {
    var w = S.want[name];
    if (!w || !canSynth()) return;
    var inst = S.beds[name];
    if (inst) {
      var tn = S.ctx.currentTime;
      try { inst.g.gain.cancelScheduledValues(tn); inst.g.gain.setTargetAtTime(BED_LEVEL[name] * w.volume, tn, 0.3); } catch (e) {}
      startSched(inst);
      return;
    }
    if (S.bedPending[name]) return;
    S.bedPending[name] = true;
    var c = S.ctx;
    prepare(c, BED_NEEDS[name], function () {
      S.bedPending[name] = false;
      if (S.ctx !== c || !S.want[name] || S.beds[name] || !canSynth()) return;
      buildBed(name);
    });
  }
  function buildBed(name) {
    var c = S.ctx, w = S.want[name];
    touchCtx();
    var g = c.createGain();
    g.gain.value = 0;
    g.connect(S.bus.beds);
    var k = mkK(c, g, c.currentTime + 0.02, {}, 'bed:' + name, S.bus.revReady ? S.bus.revIn : null);
    BEDS[name](k);
    var fadeS = Math.max(0.05, w.fade / 1000);
    g.gain.setValueAtTime(0, k.t);
    g.gain.setTargetAtTime(BED_LEVEL[name] * w.volume, k.t, fadeS / 3);
    var inst = { name: name, g: g, k: k, timer: 0, nextT: k.t + 1.2, dead: false };
    S.beds[name] = inst;
    startSched(inst);
  }
  function stopBed(name, fadeMs) {
    var inst = S.beds[name];
    if (!inst) return;
    delete S.beds[name];
    inst.dead = true;
    stopSched(inst);
    S.lastAct = now();
    var c = S.ctx;
    if (!c) return;
    var tn = c.currentTime, f = Math.max(0.03, fadeMs / 1000), end = tn + f * 1.3 + 0.05;
    try { inst.g.gain.cancelScheduledValues(tn); inst.g.gain.setTargetAtTime(0, tn, f / 4); } catch (e) {}
    inst.k.srcs.forEach(function (s) { try { s.stop(end); } catch (e) {} });
    later(function () { try { inst.g.disconnect(); } catch (e) {} }, (f * 1.3 + 0.4) * 1000);
    if (!S.idleTimer && !S.route) S.idleTimer = later(idleCheck, 15000);
  }
  function startSched(inst) { if (!inst.k.sched || inst.timer || inst.dead) return; tickSched(inst); }
  function stopSched(inst) { if (inst.timer) { clearTimeout(inst.timer); inst.timer = 0; } }
  function tickSched(inst) {
    inst.timer = 0;
    if (inst.dead || !canSynth()) return;
    var c = S.ctx, horizon = c.currentTime + 2.5;
    if (inst.nextT < c.currentTime) inst.nextT = c.currentTime + 0.1;
    while (inst.nextT < horizon) {
      try { inst.k.sched(inst.nextT); } catch (e) { report(e); }
      inst.nextT += inst.k.gap(inst.k.rnd);
    }
    inst.timer = setTimeout(function () { tickSched(inst); }, 1200);
  }

  /* ------------------------------------------------------------------ lifecycle */

  var LST = [];
  function on(target, ev, fn, capture) {
    try { target.addEventListener(ev, fn, capture ? { capture: true, passive: true } : false); }
    catch (e) { try { target.addEventListener(ev, fn, !!capture); } catch (e2) { return; } }
    LST.push([target, ev, fn, !!capture]);
  }
  function listen(yes) {
    if (yes && !S.listening) {
      S.listening = true;
      on(doc, 'visibilitychange', onVis);
      on(win, 'pagehide', onPageHide);
      on(win, 'pageshow', onVis);
      /* Some in-app browsers hand the page back from another app without a visibility event; focus still comes. */
      on(win, 'focus', onVis);
      on(doc, 'touchend', onGesture, true);
      on(doc, 'click', onGesture, true);
      on(doc, 'keydown', onGesture, true);
    } else if (!yes && S.listening) {
      S.listening = false;
      LST.forEach(function (l) { try { l[0].removeEventListener(l[1], l[2], l[3]); } catch (e) {} });
      LST = [];
    }
  }
  function onVis() { try { setHidden(!!doc.hidden); } catch (e) { report(e); } }
  function onPageHide() { try { setHidden(true); } catch (e) { report(e); } }
  function setHidden(h) {
    if (h === S.hidden) return;
    S.hidden = h;
    if (!S.unlocked || S.destroyed) return;
    if (h) hideAll(); else showAll();
  }
  function hideAll() {
    for (var k in S.tracks) {
      var t = S.tracks[k];
      if (t.waiting && t.cancelWait) t.cancelWait();
      if (k !== S.cur) { t.target = 0; t.phi = 0; }
      haltTrack(t);
    }
    pauseFiles();
    for (var b in S.beds) stopSched(S.beds[b]);
    suspendCtx();
  }
  function showAll() {
    if (S.muted) return;
    resumeCtx();
    for (var n in S.want) { if (!S.beds[n]) startBed(n); else startSched(S.beds[n]); }
    if (S.cur) resumeCurrent(700);
    /* Back from another app: a phone can hand the page back with its context still interrupted, or with a routed
       track playing into silence. Either one is fixed by the next tap (revive). */
    later(checkAfterReturn, 1800);
  }
  function checkAfterReturn() {
    if (!S.unlocked || S.hidden || S.muted || S.destroyed) return;
    var c = S.ctx, t = S.cur && S.tracks[S.cur];
    if (c && c.state !== 'running' && (anyBed() || (t && t.routed))) { S.reviveOnTap = true; return; }
    if (!t || !t.el) return;
    if (t.el.paused) { if (t.target === 1 && !t.waiting) S.reviveOnTap = true; return; }
    if (t.routed) probeSilent(t, function (silent) { if (silent) S.reviveOnTap = true; });
  }
  /* Listens to one routed track for about two seconds: true when it plays but nothing comes out. */
  function probeSilent(t, cb) {
    var c = S.ctx, an, sink, node = t.node;
    if (!c || !node) { cb(false); return; }
    try {
      an = c.createAnalyser(); an.fftSize = 1024;
      sink = c.createGain(); sink.gain.value = 0;
      node.connect(an); an.connect(sink); sink.connect(c.destination);
    } catch (e) { cb(false); return; }
    var fl = !!an.getFloatTimeDomainData, buf = fl ? new Float32Array(an.fftSize) : new Uint8Array(an.fftSize), reads = 0;
    function finish(v) { try { node.disconnect(an); } catch (e) {} try { an.disconnect(); sink.disconnect(); } catch (e) {} cb(v); }
    function read() {
      if (S.destroyed || S.hidden || S.muted || !t.el || t.el.paused || t.node !== node) { finish(false); return; }
      var i, heard = false;
      if (fl) { an.getFloatTimeDomainData(buf); for (i = 0; i < buf.length; i++) if (buf[i] > 1e-5 || buf[i] < -1e-5) { heard = true; break; } }
      else { an.getByteTimeDomainData(buf); for (i = 0; i < buf.length; i++) if (buf[i] !== 128) { heard = true; break; } }
      if (heard) { finish(false); return; }
      if (++reads >= 8) { finish(true); return; }
      later(read, 250);
    }
    later(read, 250);
  }
  /* Brings the sound back: after another app (on the next tap) and on every unmute. It re-reads the page's
     visibility, wakes the context, restarts the wanted beds and the current screen's music. Inside a tap (fresh),
     every routed track and effect gets a new element and a new media source, blessed in that tap, so a source that
     went silent after an interruption is replaced, and the current music starts again from the top. */
  function revive(fresh) {
    if (!S.unlocked || S.destroyed) return;
    S.hidden = !!doc.hidden;
    S.reviveOnTap = false;
    if (S.muted || S.hidden) return;
    var was = S.inGesture;
    if (fresh) S.inGesture = true;
    try {
      resumeCtx();
      var c = S.ctx, k, i;
      if (c && S.bus) { try { S.bus.out.gain.cancelScheduledValues(c.currentTime); S.bus.out.gain.setTargetAtTime(1, c.currentTime, 0.03); } catch (e) {} }
      if (fresh && S.route) {
        /* WebKit remakes only the current track here; the others are readied again one per tap (blessNext), and its
           effects play from memory. */
        for (k in S.tracks) {
          var t = S.tracks[k];
          if (!t.routed) continue;
          dropTrackEl(t);
          if (!S.gate || S.cur === k) ensureEl(t);
          t.phi = 0; t.target = S.cur === k ? 1 : 0; t.needsStart = S.cur === k;
        }
        for (k in S.pools) { var p = S.pools[k]; for (i = 0; i < p.els.length; i++) if (p.els[i] && p.els[i].routed) { dropFileEl(p, i); if (!S.gate) ensureFileEl(p, i); } }
        S.health = '';
      }
      if (fresh) blessAll();
      for (var n in S.want) { if (!S.beds[n]) startBed(n); else startSched(S.beds[n]); }
      if (S.cur) resumeCurrent(fresh ? 900 : 700);
      kick();
    } catch (e) { report(e); }
    S.inGesture = was;
  }
  /* A tap is only a real user gesture while the browser says so; older phones without userActivation are trusted. */
  function inTap() { try { if (nav.userActivation) return !!nav.userActivation.isActive; } catch (e) {} return true; }
  /* Every tap after unlock: bless new elements, wake an interrupted context and
     retry a track the browser refused to start. Cheap when nothing is pending. */
  function onGesture(ev) {
    if (!S.unlocked || S.destroyed) return;
    /* A page that missed its "visible again" event still gets its sound back on the first tap. */
    if (S.hidden && !doc.hidden) { S.hidden = false; showAll(); }
    if (S.reviveOnTap && !S.muted && !S.hidden) { revive(true); return; }
    S.inGesture = true;
    try {
      if (S.needBless) blessAll();
      /* WebKit: one more track per tap, on its click, so a tap counts once and typing never pays for it. */
      if (S.gate && ev && ev.type === 'click') blessNext();
      if (!S.muted && !S.hidden) {
        if (S.ctx && S.ctx.state !== 'running') resumeCtx();
        var t = S.cur && S.tracks[S.cur];
        if (t && t.el && t.el.paused && (t.target === 1 || t.waiting)) { startTrack(t, false); if (!t.waiting) waitThenCross(t); }
      }
    } catch (e) { report(e); }
    S.inGesture = false;
  }

  function unlock() {
    if (S.destroyed) S.destroyed = false;
    S.inGesture = true;
    try {
      var c = makeCtx();
      if (c) { resumeCtx(); playSilent(c); }
      /* Web Audio follows the ringer switch on iOS while <audio> does not; ask for the
         playback session (iOS 16.4+) so synth effects behave like the music. */
      try { if (nav.audioSession && nav.audioSession.type !== 'playback') nav.audioSession.type = 'playback'; } catch (e) {}
      if (!S.unlocked) {
        S.unlocked = true;
        S.volFixed = !volumeWritable();
        S.route = !!(c && c.createMediaElementSource) && (cfg.route === 'always' || (cfg.route !== 'never' && S.volFixed));
        var key, i;
        /* WebKit makes a track's element when that track is readied (blessNext) and plays effects from memory, so
           this tap starts one element instead of every one. */
        for (key in MUSIC) {
          var t = track(key);
          if (t.el && S.route && !t.routed) dropTrackEl(t);
        }
        for (key in FILES) {
          var p = pool(key);
          for (i = 0; i < 2; i++) if (p.els[i] && S.route && !p.els[i].routed) dropFileEl(p, i);
        }
        /* Chromium and Gecko keep one element per sound. They unlock the whole document, so the elements are made just
           after the tap's frame and play as well; a sound asked for sooner makes its own at once (startTrack, playFile). */
        if (!S.gate) afterPaint(function () {
          if (S.destroyed) return;
          var k, j;
          for (k in MUSIC) ensureEl(track(k));
          for (k in FILES) { var q = pool(k); for (j = 0; j < 2; j++) ensureFileEl(q, j); }
        });
        listen(true);
        /* The synth tables need no tap: they are filled after the tap's frame (a sound before then fills its own). */
        if (c) afterPaint(function () { if (S.ctx === c && !S.destroyed) warmUp(c); });
        if (S.gate && c) later(warmBufs, 600);
      }
      blessAll();
      blessNext();
      if (S.cur && !S.muted && !S.hidden) resumeCurrent((S.tracks[S.cur] && S.tracks[S.cur].fade) || cfg.fade);
      if (!S.muted) for (var n in S.want) startBed(n);
    } catch (e) { report(e); }
    S.inGesture = false;
    return S.unlocked;
  }

  function setMuted(m) {
    m = !!m;
    if (m === S.muted) return m;
    S.muted = m;
    writeMuted(m);
    var c = S.ctx;
    if (m) {
      if (c && S.bus) { try { S.bus.out.gain.setTargetAtTime(0, c.currentTime, 0.03); } catch (e) {} }
      for (var n in S.beds) stopBed(n, 250);
      pauseFiles();
      later(function () { if (S.muted) suspendCtx(); }, 450);
    } else if (S.unlocked && !S.destroyed) {
      /* Unmute always brings back the current screen's music and beds, even after another app left the context
         interrupted or a routed source silent (Jan, 30 September). */
      revive(inTap());
    }
    kick();
    emit('mute', { muted: m });
    return m;
  }

  function prefetch(key) {
    key = String(key || '');
    var i;
    if (has(MUSIC, key)) {
      var t = track(key);
      t.preload = 'auto';
      var el = ensureEl(t);
      if (el) {
        if (el.preload !== 'auto') el.preload = 'auto';
        if (el.paused && el.readyState === 0 && el.networkState === 0) { try { el.load(); } catch (e) {} }
      }
      return true;
    }
    if (has(FILES, key)) {
      /* WebKit plays effects from memory: load the buffer; an element would only wait for a tap of its own. */
      if (S.gate && S.ctx) { loadBuf(key); return true; }
      var p = pool(key);
      p.preload = 'auto';
      var e0 = ensureFileEl(p, 0);
      for (i = 1; i < 2; i++) ensureFileEl(p, i);
      if (BUF[key] && S.ctx) { loadBuf(key); return true; }
      if (e0 && e0.el.preload !== 'auto') e0.el.preload = 'auto';
      if (e0 && e0.el.paused && e0.el.readyState === 0) { try { e0.el.load(); } catch (e) {} }
      return true;
    }
    if (has(BEDS, key)) {
      if (S.ctx) prepare(S.ctx, BED_NEEDS[key], noop);
      return true;
    }
    if (has(SYN, key)) { if (S.ctx) warmUp(S.ctx); return true; }
    return false;
  }

  function sfx(name, o) {
    o = o || {};
    if (!name) return false;
    name = String(name);
    var forceSynth = name.indexOf('synth:') === 0;
    if (forceSynth) name = name.slice(6);
    if (!forceSynth && has(FILES, name)) return playFile(name, o);
    if (has(SYN, name)) return playSynth(name, o);
    if (has(BEDS, name)) { warnOnce('bedsfx:' + name, '"' + name + '" is a bed: use QOAudio.bed("' + name + '", true)'); return false; }
    warnOnce('sfx:' + name, 'unknown sound "' + name + '"');
    return false;
  }

  function buzz(pattern) {
    try {
      if (!nav.vibrate || S.hidden || !S.unlocked) return false;
      /* Chrome logs an intervention error for vibrate() before the first real tap. */
      if (nav.userActivation && !nav.userActivation.hasBeenActive) return false;
      var p = typeof pattern === 'string' ? BUZZ[pattern] : pattern;
      if (p == null) p = 10;
      return !!nav.vibrate(p);
    } catch (e) { return false; }
  }

  function list() { return { music: Object.keys(MUSIC), files: Object.keys(FILES), synth: Object.keys(SYN), beds: Object.keys(BEDS) }; }

  function state() {
    var t = S.cur && S.tracks[S.cur], levels = {};
    for (var k in S.tracks) {
      var x = S.tracks[k];
      if (x.el) levels[k] = { level: Math.round(Math.max(0, x.v) * 1000) / 1000, phase: Math.round(x.phi * 1000) / 1000, playing: !x.el.paused, waiting: x.waiting, routed: x.routed, error: x.el.error ? x.el.error.code : 0 };
    }
    return {
      tracks: levels,
      version: VERSION, unlocked: S.unlocked, muted: S.muted, hidden: S.hidden,
      music: S.cur, musicPlaying: !!(t && t.el && !t.el.paused), musicLevel: t ? Math.round(Math.max(0, t.v) * 1000) / 1000 : 0,
      duck: Math.round(S.duckD * 100) / 100, routed: S.route, volumeFixed: S.volFixed, health: S.health,
      context: S.ctx ? S.ctx.state : 'none', reverb: !!(S.bus && S.bus.revReady),
      beds: Object.keys(S.beds), voices: S.voices,
      /* For checks: which sounds play from memory, and (WebKit) which tracks a tap has readied. */
      buffers: Object.keys(S.bufs || {}).filter(function (k) { return !!(S.bufs[k] && S.bufs[k].buf); }),
      blessed: Object.keys(S.tracks).filter(function (k) { return !!(S.tracks[k].el && S.tracks[k].el._qoBlessed); }),
      perElement: S.gate
    };
  }

  function configure(o) {
    o = o || {};
    var k, t, p, i;
    if (o.musicVolume != null) cfg.musicVolume = clamp(num(o.musicVolume, cfg.musicVolume), 0, 1);
    if (o.sfxVolume != null) cfg.sfxVolume = clamp(num(o.sfxVolume, cfg.sfxVolume), 0, 2);
    if (o.duckDepth != null) cfg.duckDepth = clamp(num(o.duckDepth, cfg.duckDepth), 0, 1);
    if (o.fade != null) cfg.fade = Math.max(0, num(o.fade, cfg.fade));
    if (o.route === 'auto' || o.route === 'always' || o.route === 'never') cfg.route = o.route;
    if (o.music) for (k in o.music) {
      if (!has(o.music, k) || typeof o.music[k] !== 'string') continue;
      MUSIC[k] = o.music[k];
      t = S.tracks[k];
      if (t && t.url !== MUSIC[k]) { dropTrackEl(t); t.url = MUSIC[k]; t.plain = false; if (S.unlocked && !S.gate) ensureEl(t); }
    }
    if (o.files) for (k in o.files) {
      if (!has(o.files, k) || typeof o.files[k] !== 'string') continue;
      FILES[k] = o.files[k];
      p = S.pools[k];
      if (p && p.url !== FILES[k]) { for (i = 0; i < p.els.length; i++) dropFileEl(p, i); p.url = FILES[k]; p.plain = false; if (S.bufs) delete S.bufs[k]; if (S.unlocked && !S.gate) { ensureFileEl(p, 0); ensureFileEl(p, 1); } }
    }
    if (o.fileVolumes) for (k in o.fileVolumes) if (has(o.fileVolumes, k)) FILE_VOL[k] = clamp(num(o.fileVolumes[k], 0.6), 0, 1);
    kick();
    return { musicVolume: cfg.musicVolume, sfxVolume: cfg.sfxVolume, duckDepth: cfg.duckDepth, fade: cfg.fade, route: cfg.route };
  }

  /* Render a synth recipe or bed through the same output chain into an
     OfflineAudioContext (for tests and waveform previews). Resolves an AudioBuffer or null. */
  function renderOffline(name, o) {
    o = o || {};
    return new Promise(function (resolve) {
      try {
        var OAC = win.OfflineAudioContext || win.webkitOfflineAudioContext;
        name = String(name || '');
        if (!OAC || !(has(SYN, name) || has(BEDS, name))) { resolve(null); return; }
        var sr = num(o.sampleRate, 44100), secs = num(o.seconds, has(BEDS, name) ? 4 : 3);
        var c = new OAC(2, Math.ceil(sr * secs), sr), bus = buildBus(c), ir = irEntry(c), k;
        ir.step(Infinity);
        bus.conv.buffer = ir.buf;
        if (has(SYN, name)) {
          var out = c.createGain();
          out.gain.value = clamp(num(o.volume, 1), 0, 4);
          out.connect(bus.sfx);
          k = mkK(c, out, 0.01, o, name, bus.revIn);
          SYN[name](k);
        } else {
          var g = c.createGain();
          g.gain.value = BED_LEVEL[name] * clamp(num(o.volume, 1), 0, 2);
          g.connect(bus.beds);
          k = mkK(c, g, 0, {}, 'bed:' + name, bus.revIn);
          BEDS[name](k);
          if (k.sched) { var tt = 0.6; while (tt < secs) { k.sched(tt); tt += k.gap(k.rnd); } }
        }
        var settled = false;
        var done = function (b) { if (!settled) { settled = true; resolve(b || null); } };
        c.oncomplete = function (ev) { done(ev && ev.renderedBuffer); };
        var p = c.startRendering();
        if (p && p.then) p.then(done, function () { done(null); });
      } catch (e) { report(e); resolve(null); }
    });
  }

  function destroy() {
    S.destroyed = true;
    listen(false);
    tk.on = false;
    if (tk.raf && win.cancelAnimationFrame) win.cancelAnimationFrame(tk.raf);
    clearTimeout(tk.to); clearTimeout(tk.hold);
    clearTimeout(S.idleTimer); S.idleTimer = 0;
    var k, i;
    for (k in S.beds) { S.beds[k].dead = true; stopSched(S.beds[k]); }
    for (k in S.tracks) dropTrackEl(S.tracks[k]);
    for (k in S.pools) for (i = 0; i < S.pools[k].els.length; i++) dropFileEl(S.pools[k], i);
    jobs.length = 0;
    if (S.ctx) { try { var p = S.ctx.close(); if (p && p.catch) p.catch(noop); } catch (e) {} }
    S.ctx = null; S.bus = null; S.tracks = {}; S.pools = {}; S.beds = {}; S.want = {}; S.bedPending = {};
    S.cur = null; S.unlocked = false; S.route = false; S.health = ''; S.voices = 0; S.last = {};
    S.duckD = 0; S.duckUntil = 0; S.muteM = S.muted ? 0 : 1; S.needBless = false;
    S.srcs = []; S.bufsWarm = false;
    return true;
  }

  /* ------------------------------------------------------------------ public API */

  function guard(fn, fallback) {
    return function () {
      try { return fn.apply(null, arguments); } catch (e) { report(e); return fallback; }
    };
  }
  var api = {
    __qo2: true,
    version: VERSION,
    unlock: guard(unlock, false),
    music: guard(music, false),
    prefetch: guard(prefetch, false),
    duck: guard(duck, false),
    unduck: guard(unduck, false),
    sfx: guard(sfx, false),
    bed: guard(bed, false),
    setMuted: guard(setMuted, false),
    toggleMuted: guard(function () { return setMuted(!S.muted); }, false),
    buzz: guard(buzz, false),
    list: guard(list, { music: [], files: [], synth: [], beds: [] }),
    state: guard(state, null),
    configure: guard(configure, null),
    renderOffline: function (name, o) { try { return renderOffline(name, o); } catch (e) { report(e); return Promise.resolve(null); } },
    destroy: guard(destroy, false)
  };
  try {
    Object.defineProperty(api, 'muted', { enumerable: true, get: function () { return S.muted; }, set: function (v) { api.setMuted(v); } });
    Object.defineProperty(api, 'current', { enumerable: true, get: function () { return S.cur; } });
  } catch (e) {}
  win.QOAudio = api;
})(typeof window !== 'undefined' ? window : this);
